import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Select,
  MenuItem,
  InputLabel,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Collapse,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Autocomplete,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
} from '@mui/material';
import {
  Print as PrintIcon,
  FileDownload as ExcelIcon,
  PictureAsPdf as PdfIcon,
  Refresh as RefreshIcon,
  People as EmployeesIcon,
  Work as WorkIcon,
  BusinessCenter as BusinessCenterIcon,
  AccountBalance as BankIcon,
  Money as CashIcon,
  TrendingUp as EarningsIcon,
  TrendingDown as DeductionsIcon,
  KeyboardArrowDown,
  KeyboardArrowUp,
  EventBusy as AbsenceIcon,
  Receipt as ReceiptIcon,
  ReceiptLong as SlipsIcon,
  LocalAtm as SalaryIcon,
  Home as HousingIcon,
  DirectionsCar as TransportIcon,
  CardGiftcard as AllowanceIcon,
  AccessTime as OvertimeIcon,
  WbSunny as HolidayIcon,
  EventAvailable as OffDayIcon,
  RemoveCircle as DeductionIcon,
  AccountBalanceWallet as NetIcon,
  CloudDownload as CloudDownloadIcon,
} from '@mui/icons-material';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import payrollsService from '../services/payrolls.service';
import { companyInfoService, CompanyInfo } from '../services/companyInfo.service';
import { toast } from 'react-toastify';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import autoTable from 'jspdf-autotable';
import config from '../config';

const translations = {
  en: {
    title: 'Payroll Report',
    titleAr: 'تقرير الرواتب',
    selectPayroll: 'Select Payroll',
    payrollNumber: 'Payroll #',
    period: 'Period',
    type: 'Type',
    management: 'Management',
    branch: 'Branch',
    print: 'Print',
    exportExcel: 'Export Excel',
    exportPdf: 'Export PDF',
    exportBankFile: 'Export Bank File (Workers)',
    refresh: 'Refresh',
    payslip: 'Payslip',
    printEmployeeSlips: 'Print Employee Slips',
    payrollDate: 'Payroll Date',
    selectEmployees: 'Select Employees',
    printSelected: 'Print Selected',
    selectAll: 'Select All',
    deselectAll: 'Deselect All',
    financialManager: 'Financial Manager',
    hrManager: 'HR Manager',
    boardChairman: 'Board Chairman',
    employeeCode: 'Code',
    employeeName: 'Employee',
    branchName: 'Branch',
    basicSalary: 'Basic Salary',
    housing: 'Housing',
    transport: 'Transport',
    other: 'Other',
    overtime: 'Overtime',
    offDay: 'Off Day',
    holiday: 'Holiday',
    totalEarnings: 'Total Earnings',
    earnings: 'Earnings',
    absences: 'Absences',
    penalties: 'Penalties & Loan',
    otherDed: 'Other Ded.',
    totalDeductions: 'Total Deductions',
    deductions: 'Deductions',
    netSalary: 'Net Salary',
    bank: 'Bank',
    cash: 'Cash',
    printFilter: 'Print Filter',
    printAll: 'All (Bank + Cash)',
    printBankOnly: 'Bank Only',
    printCashOnly: 'Cash Only',
    summary: 'Summary',
    totalEmployees: 'Total Employees',
    totalBank: 'Total Bank',
    totalCash: 'Total Cash',
    details: 'Details',
    showDetails: 'Show Details',
    hideDetails: 'Hide Details',
    absenceDetails: 'Absence Details',
    deductionDetails: 'Deduction Details',
    date: 'Date',
    absenceType: 'Type',
    deductionType: 'Type',
    days: 'Days',
    hours: 'Hours',
    amount: 'Amount',
    reason: 'Reason',
    reference: 'Reference',
    installment: 'Installment',
    loading: 'Loading...',
    noData: 'No payroll selected',
    kwd: 'KWD',
    asOf: 'As of',
    hrs: 'hrs',
    total: 'Total',
  },
  ar: {
    title: 'تقرير الرواتب',
    titleAr: 'تقرير الرواتب',
    selectPayroll: 'اختر الراتب',
    payrollNumber: 'رقم الراتب',
    period: 'الفترة',
    type: 'النوع',
    management: 'إدارة',
    branch: 'فرع',
    print: 'طباعة',
    exportExcel: 'تصدير Excel',
    exportPdf: 'تصدير PDF',
    exportBankFile: 'تصدير ملف البنك (عمال)',
    refresh: 'تحديث',
    payslip: 'قسيمة راتب',
    printEmployeeSlips: 'طباعة قسائم الموظفين',
    payrollDate: 'تاريخ الراتب',
    selectEmployees: 'اختر الموظفين',
    printSelected: 'طباعة المحدد',
    selectAll: 'تحديد الكل',
    deselectAll: 'إلغاء التحديد',
    financialManager: 'المدير المالي',
    hrManager: 'مدير الموارد البشرية',
    boardChairman: 'رئيس مجلس الإدارة',
    employeeCode: 'الكود',
    employeeName: 'الموظف',
    branchName: 'الفرع',
    basicSalary: 'الراتب الأساسي',
    housing: 'سكن',
    transport: 'نقل',
    other: 'أخرى',
    overtime: 'إضافي',
    offDay: 'كانسل أوف',
    holiday: 'عطلة',
    totalEarnings: 'إجمالي الاستحقاقات',
    earnings: 'الاستحقاقات',
    absences: 'غياب',
    penalties: 'جزاءات وقروض',
    otherDed: 'خصومات أخرى',
    totalDeductions: 'إجمالي الخصومات',
    deductions: 'الخصومات',
    netSalary: 'صافي الراتب',
    bank: 'بنك',
    cash: 'نقد',
    printFilter: 'فلتر الطباعة',
    printAll: 'الكل (بنك + كاش)',
    printBankOnly: 'البنك فقط',
    printCashOnly: 'الكاش فقط',
    summary: 'الملخص',
    totalEmployees: 'إجمالي الموظفين',
    totalBank: 'إجمالي البنك',
    totalCash: 'إجمالي النقد',
    details: 'التفاصيل',
    showDetails: 'إظهار التفاصيل',
    hideDetails: 'إخفاء التفاصيل',
    absenceDetails: 'تفاصيل الغياب',
    deductionDetails: 'تفاصيل الخصومات',
    date: 'التاريخ',
    absenceType: 'النوع',
    deductionType: 'النوع',
    days: 'أيام',
    hours: 'ساعات',
    amount: 'المبلغ',
    reason: 'السبب',
    reference: 'المرجع',
    installment: 'قسط',
    loading: 'جاري التحميل...',
    noData: 'لم يتم اختيار راتب',
    kwd: 'د.ك',
    asOf: 'حتى تاريخ',
    hrs: 'س',
    total: 'المجموع',
  },
};



interface PayrollReportProps {
  payrollId?: number;
}

const PayrollReport: React.FC<PayrollReportProps> = ({ payrollId: initialPayrollId }) => {
  const { language } = useLanguage();
  const { mode } = useTheme();
  const t = translations[language as keyof typeof translations];
  const isRTL = language === 'ar';

  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState<any | null>(null);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [payrollCompanyName, setPayrollCompanyName] = useState<string>('');
  const [payrolls, setPayrolls] = useState<any[]>([]);
  const [selectedPayrollId, setSelectedPayrollId] = useState<number | null>(initialPayrollId || null);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [employeeSelectionDialog, setEmployeeSelectionDialog] = useState<'none' | 'pdf' | 'slips'>('none');
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<Set<number>>(new Set());
  // Payment-method print filter: '' = all (default), 'bank' = bank employees only, 'cash' = cash employees only
  // Applies to: regular PDF, browser Print, and Employee Slips (cash slips are usually what gets printed for distribution)
  const [printPaymentFilter, setPrintPaymentFilter] = useState<'' | 'bank' | 'cash'>('');
  const [burganDateDialog, setBurganDateDialog] = useState(false);
  const [burganMonth, setBurganMonth] = useState<number>(new Date().getMonth() + 1);
  const [burganYear, setBurganYear] = useState<number>(new Date().getFullYear());
  const [staffDateDialog, setStaffDateDialog] = useState(false);
  const [staffMonth, setStaffMonth] = useState<number>(new Date().getMonth() + 1);
  const [staffYear, setStaffYear] = useState<number>(new Date().getFullYear());
  const [payrollTypeFilter, setPayrollTypeFilter] = useState<string>('Worker');
  const [viewMode, setViewMode] = useState<'payroll' | 'monthly'>('payroll');
  const [monthlyViewMonth, setMonthlyViewMonth] = useState<number>(new Date().getMonth() + 1);
  const [monthlyViewYear, setMonthlyViewYear] = useState<number>(new Date().getFullYear());

  useEffect(() => {
    loadCompanyInfo();
    loadPayrollCompanyName();
    loadPayrolls();
  }, []);

  const loadPayrollCompanyName = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${config.API_BASE_URL}/SystemSettings/PayrollCompanyName`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        if (data && data.value) {
          setPayrollCompanyName(data.value);
        }
      }
    } catch (error) {
      // Silent fail — use default company name
    }
  };

  useEffect(() => {
    if (selectedPayrollId) {
      generateReport(selectedPayrollId);
    }
  }, [selectedPayrollId]);

  const loadCompanyInfo = async () => {
    try {
      const info = await companyInfoService.getCompanyInfo();
      setCompanyInfo(info);
    } catch (error) {
      console.error('Failed to load company info:', error);
    }
  };

  const loadPayrolls = async () => {
    try {
      const data = await payrollsService.getAll();
      setPayrolls(data);
      
      // Auto-select the most recent payroll if not already selected
      if (!selectedPayrollId && data.length > 0) {
        const mostRecent = data[0]; // Assuming data is sorted by date descending
        setSelectedPayrollId(mostRecent.payrollId);
      }
    } catch (error) {
      console.error('Failed to load payrolls:', error);
      toast.error(isRTL ? 'فشل تحميل الرواتب' : 'Failed to load payrolls');
    }
  };

  const generateReport = async (payrollId: number) => {
    try {
      setLoading(true);
      const response = await payrollsService.getReport(payrollId);
      setReportData(response);
      setViewMode('payroll');
    } catch (error) {
      console.error('Failed to generate report:', error);
      toast.error(isRTL ? 'فشل تحميل التقرير' : 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };
  const loadMonthlyData = async () => {
    try {
      setLoading(true);
      toast.info(isRTL ? 'جاري جلب بيانات الشهر...' : 'Loading monthly data...');

      // جلب جميع الرواتب
      const allPayrolls = await payrollsService.getAll();
      
      // تصفية الرواتب للشهر المختار (استخدام UTC)
      const monthlyPayrolls = allPayrolls.filter((p: any) => {
        const pEnd = new Date(p.periodEnd);
        return pEnd.getUTCMonth() + 1 === monthlyViewMonth && pEnd.getUTCFullYear() === monthlyViewYear;
      });

      if (monthlyPayrolls.length === 0) {
        toast.warning(isRTL ? 'لا توجد رواتب لهذا الشهر' : 'No payrolls found for this month');
        setReportData(null);
        setLoading(false);
        return;
      }

      // جمع جميع الموظفين من كل الرواتب
      let allEmployees: any[] = [];
      
      for (const payroll of monthlyPayrolls) {
        const payrollReport = await payrollsService.getReport(payroll.payrollId);
        if (payrollReport && payrollReport.employees) {
          allEmployees = allEmployees.concat(payrollReport.employees);
        }
      }

      // تصفية حسب النوع والراتب > 0
      const filteredEmployees = allEmployees.filter((emp: any) => 
        emp.bankFileType === payrollTypeFilter && emp.netSalary > 0
      );

      if (filteredEmployees.length === 0) {
        toast.warning(isRTL ? 'لا يوجد موظفين لهذا النوع في هذا الشهر' : 'No employees of this type found for this month');
        setReportData(null);
        setLoading(false);
        return;
      }

      // حساب الإجماليات
      const totalBasic = filteredEmployees.reduce((sum: number, e: any) => sum + e.basicSalary, 0);
      const totalEarnings = filteredEmployees.reduce((sum: number, e: any) => sum + e.totalEarnings, 0);
      const totalDeductions = filteredEmployees.reduce((sum: number, e: any) => sum + e.totalDeductions, 0);
      const totalNet = filteredEmployees.reduce((sum: number, e: any) => sum + e.netSalary, 0);
      const totalBankAmt = filteredEmployees.reduce((sum: number, e: any) => sum + e.bankAmount, 0);
      const totalCashAmt = filteredEmployees.reduce((sum: number, e: any) => sum + e.cashAmount, 0);

      // إنشاء reportData للعرض الشهري
      const firstDay = new Date(monthlyViewYear, monthlyViewMonth - 1, 1);
      const lastDay = new Date(monthlyViewYear, monthlyViewMonth, 0);
      
      const monthlyReport = {
        payrollId: 0,
        payrollNumber: `MONTHLY-${monthlyViewMonth.toString().padStart(2, '0')}-${monthlyViewYear}`,
        payrollMonth: monthlyViewMonth,
        payrollYear: monthlyViewYear,
        type: 'Monthly',
        periodStart: firstDay.toISOString(),
        periodEnd: lastDay.toISOString(),
        totalEmployees: filteredEmployees.length,
        totalBasicSalary: totalBasic,
        totalEarnings: totalEarnings,
        totalDeductions: totalDeductions,
        totalNetSalary: totalNet,
        totalBankAmount: totalBankAmt,
        totalCashAmount: totalCashAmt,
        employees: filteredEmployees
      };

      setReportData(monthlyReport);
      setViewMode('monthly');
      toast.success(isRTL 
        ? `تم تحميل ${filteredEmployees.length} موظف` 
        : `Loaded ${filteredEmployees.length} employees`
      );
    } catch (error) {
      console.error('Failed to load monthly data:', error);
      toast.error(isRTL ? 'فشل تحميل البيانات' : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };


  const toggleRow = (employeeId: number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(employeeId)) {
      newExpanded.delete(employeeId);
    } else {
      newExpanded.add(employeeId);
    }
    setExpandedRows(newExpanded);
  };

  const formatCurrency = (value: number) => `${value.toFixed(3)} ${t.kwd}`;
  const formatNumber = (value: number) => value.toFixed(3);
  
  // دالة لتنسيق التاريخ بشكل صحيح (تجنب مشاكل timezone)
  const formatDate = (dateString: string) => {
    if (dateString && dateString.includes('-')) {
      const parts = dateString.split('T')[0].split('-');
      if (parts.length === 3) {
        const [year, month, day] = parts;
        return `${day}/${month}/${year}`;
      }
    }
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${day}/${month}/${year}`;
  };

  // دالة لعرض الفترة - إذا شهر كامل، عرض "شهر يناير 2026"
  const formatPeriod = (startDate: string, endDate: string) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    const startYear = start.getUTCFullYear();
    const startMonth = start.getUTCMonth() + 1;
    const startDay = start.getUTCDate();
    
    const endYear = end.getUTCFullYear();
    const endMonth = end.getUTCMonth() + 1;
    const endDay = end.getUTCDate();
    
    // التحقق من شهر كامل (يبدأ من 1 وينتهي في آخر يوم من الشهر)
    const lastDayOfMonth = new Date(Date.UTC(endYear, endMonth, 0)).getUTCDate();
    
    if (startDay === 1 && endDay === lastDayOfMonth && startMonth === endMonth && startYear === endYear) {
      // شهر كامل
      const monthNames = isRTL 
        ? ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر']
        : ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
      return isRTL ? `شهر ${monthNames[startMonth - 1]} ${startYear}` : `${monthNames[startMonth - 1]} ${startYear}`;
    }
    
    // فترة عادية
    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  };


  const exportToExcel = async () => {
    if (!reportData) return;
    
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(t.title);

      let currentRow = 1;

      // Company Logo
      if (companyInfo?.logoBase64) {
        try {
          const logoImage = workbook.addImage({
            base64: companyInfo.logoBase64,
            extension: 'png',
          });
          worksheet.addImage(logoImage, {
            tl: { col: 0, row: 0 },
            ext: { width: 80, height: 60 }
          });
        } catch (err) {
          console.warn('Could not add logo:', err);
        }
      }

      // Company Header
      worksheet.mergeCells('B1:L1');
      const companyCell = worksheet.getCell('B1');
      companyCell.value = isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName;
      companyCell.font = { size: 18, bold: true, color: { argb: 'FF00796B' } };
      companyCell.alignment = { horizontal: 'center', vertical: 'middle' };

      worksheet.mergeCells('B2:L2');
      const addressCell = worksheet.getCell('B2');
      addressCell.value = `📍 ${companyInfo?.address || ''}`;
      addressCell.font = { size: 9, color: { argb: 'FF424242' } };
      addressCell.alignment = { horizontal: 'center' };

      worksheet.mergeCells('B3:L3');
      const contactCell = worksheet.getCell('B3');
      contactCell.value = `📞 ${companyInfo?.phone || ''} | ✉️ ${companyInfo?.email || ''}`;
      contactCell.font = { size: 9, color: { argb: 'FF424242' } };
      contactCell.alignment = { horizontal: 'center' };

      currentRow = 5;

      // Report Title
      worksheet.mergeCells(`A${currentRow}:L${currentRow}`);
      const titleCell = worksheet.getCell(`A${currentRow}`);
      titleCell.value = `${isRTL ? t.titleAr : t.title} - ${reportData.payrollNumber}${reportData.type === 'Branch' && reportData.branchName ? ` (${isRTL ? reportData.branchNameAr : reportData.branchName})` : ''}`;
      titleCell.font = { size: 14, bold: true, color: { argb: 'FFFFFFFF' } };
      titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
      titleCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF00796B' }
      };

      currentRow++;

      // Period Info
      worksheet.mergeCells(`A${currentRow}:L${currentRow}`);
      const periodCell = worksheet.getCell(`A${currentRow}`);
      periodCell.value = `${t.period}: ${formatPeriod(reportData.periodStart, reportData.periodEnd)} | ${t.type}: ${reportData.type === 'Management' ? t.management : t.branch}`;
      periodCell.font = { size: 10, bold: true, color: { argb: 'FF00796B' } };
      periodCell.alignment = { horizontal: 'center' };

      currentRow += 2;

      // Summary Cards in Excel (4 cells)
      const summaryRow = currentRow;
      
      ['A', 'D', 'G', 'J'].forEach((col, idx) => {
        const values = [
          { label: t.totalEmployees, value: reportData.totalEmployees, color: 'FF4CAF50' },
          { label: t.totalEarnings, value: formatCurrency(reportData.totalEarnings), color: 'FF00796B' },
          { label: t.totalDeductions, value: formatCurrency(reportData.totalDeductions), color: 'FFF44336' },
          { label: t.netSalary, value: formatCurrency(reportData.totalNetSalary), color: 'FF9C27B0' }
        ];
        
        const endCol = String.fromCharCode(col.charCodeAt(0) + 2);
        worksheet.mergeCells(`${col}${summaryRow}:${endCol}${summaryRow}`);
        const cell = worksheet.getCell(`${col}${summaryRow}`);
        cell.value = `${values[idx].label}: ${values[idx].value}`;
        cell.font = { size: 10, bold: true, color: { argb: 'FFFFFFFF' } };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: values[idx].color } };
      });

      currentRow += 2;

      // Table Headers
      const headerRow = worksheet.getRow(currentRow);
      headerRow.values = [
        t.employeeCode,
        t.employeeName,
        t.basicSalary,
        t.housing,
        t.transport,
        t.overtime,
        t.offDay,
        t.holiday,
        t.totalEarnings,
        t.absences,
        t.penalties,
        t.totalDeductions,
        t.netSalary
      ];
      headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 9 };
      headerRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF00796B' }
      };
      headerRow.alignment = { horizontal: 'center', vertical: 'middle' };
      headerRow.height = 25;

      currentRow++;

      // Employee Data
      reportData.employees.forEach((emp: any) => {
        const row = worksheet.getRow(currentRow);
        row.values = [
          emp.employeeCode,
          isRTL ? emp.employeeNameAr : emp.employeeName,
          emp.basicSalary,
          emp.housingAllowance,
          emp.transportAllowance,
          emp.overtimePay,
          emp.offDayPay,
          emp.publicHolidayPay,
          emp.totalEarnings,
          emp.absencesAmount,
          emp.penaltiesAmount,
          emp.totalDeductions,
          emp.netSalary
        ];
        row.alignment = { horizontal: 'center', vertical: 'middle' };
        row.height = 20;
        
        // Number formatting for currency cells
        [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13].forEach(colNum => {
          row.getCell(colNum).numFmt = '#,##0.000';
        });
        
        // Alternate row colors
        if (currentRow % 2 === 0) {
          row.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFF5F5F5' }
          };
        }
        
        currentRow++;
        
        // Add absence details if any
        if (emp.absencesDetails && emp.absencesDetails.length > 0) {
          emp.absencesDetails.forEach((absence: any) => {
            const absRow = worksheet.getRow(currentRow);
            absRow.values = [
              '', 
              `  📅 ${new Date(absence.absenceDate).toLocaleDateString('en-GB')} - ${isRTL ? absence.absenceTypeAr : absence.absenceType}`,
              '',
              '',
              '',
              '',
              '',
              '',
              '',
              absence.deductionAmount,
              '',
              '',
              ''
            ];
            absRow.font = { size: 8, italic: true, color: { argb: 'FF666666' } };
            absRow.getCell(10).font = { size: 8, italic: true, color: { argb: 'FFF44336' } };
            absRow.getCell(10).numFmt = '#,##0.000';
            currentRow++;
          });
        }
        
        // Add deduction details if any
        if (emp.deductionsDetails && emp.deductionsDetails.length > 0) {
          emp.deductionsDetails.forEach((deduction: any) => {
            const dedRow = worksheet.getRow(currentRow);
            const installmentText = deduction.isRecurring ? 
              ` (${deduction.installmentNumber}/${deduction.totalInstallments})` : '';
            dedRow.values = [
              '', 
              `  💰 ${new Date(deduction.deductionDate).toLocaleDateString('en-GB')} - ${isRTL ? deduction.deductionTypeAr : deduction.deductionType}${installmentText}`,
              '',
              '',
              '',
              '',
              '',
              '',
              '',
              '',
              deduction.amount,
              '',
              ''
            ];
            dedRow.font = { size: 8, italic: true, color: { argb: 'FF666666' } };
            dedRow.getCell(11).font = { size: 8, italic: true, color: { argb: 'FFF44336' } };
            dedRow.getCell(11).numFmt = '#,##0.000';
            currentRow++;
          });
        }
      });

      // Column widths
      worksheet.getColumn(1).width = 12;
      worksheet.getColumn(2).width = 30;
      [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13].forEach(col => {
        worksheet.getColumn(col).width = 12;
      });

      // Save file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Payroll_Report_${reportData.payrollNumber}.xlsx`;
      link.click();

      toast.success(t.exportExcel + ' ✓');
    } catch (error) {
      console.error('Excel export failed:', error);
      toast.error(isRTL ? 'فشل تصدير Excel' : 'Excel export failed');
    }
  };

  // Apply the payment-method print filter to an employee list (no filter → return as-is)
  const applyPaymentFilter = (employees: any[]): any[] => {
    if (printPaymentFilter === 'bank') return employees.filter((e: any) => (e.bankAmount || 0) > 0);
    if (printPaymentFilter === 'cash') return employees.filter((e: any) => (e.cashAmount || 0) > 0);
    return employees;
  };

  const exportToPdf = async () => {
    if (!reportData) return;

    // Open employee selection dialog — pre-select based on payment filter
    const eligible = applyPaymentFilter(reportData.employees);
    setEmployeeSelectionDialog('pdf');
    setSelectedEmployeeIds(new Set(eligible.map((emp: any) => emp.employeeId)));
  };

  const generatePrintablePDF = async () => {
    if (!reportData || !companyInfo) return;

    const selectedEmployees = reportData.employees.filter((emp: any) => 
      selectedEmployeeIds.has(emp.employeeId)
    );

    if (selectedEmployees.length === 0) {
      toast.error(isRTL ? 'الرجاء اختيار موظف واحد على الأقل' : 'Please select at least one employee');
      return;
    }

    setEmployeeSelectionDialog('none');
    toast.info(isRTL ? 'جاري إنشاء PDF...' : 'Generating PDF...');

    const totalEarnings = selectedEmployees.reduce((sum: number, emp: any) => sum + emp.totalEarnings, 0);
    const totalDeductions = selectedEmployees.reduce((sum: number, emp: any) => sum + emp.totalDeductions, 0);
    const totalNetSalary = selectedEmployees.reduce((sum: number, emp: any) => sum + emp.netSalary, 0);

    // Create temporary container
    const tempDiv = document.createElement('div');
    tempDiv.style.position = 'absolute';
    tempDiv.style.left = '-9999px';
    tempDiv.style.width = '210mm';
    tempDiv.style.background = 'white';
    tempDiv.style.padding = '15mm';
    tempDiv.style.fontFamily = 'Arial, sans-serif';
    tempDiv.style.direction = isRTL ? 'rtl' : 'ltr';

    tempDiv.innerHTML = `
      <!-- Header -->
      <div style="text-align: center; border-bottom: 3px solid #4A90A4; padding-bottom: 10px; margin-bottom: 15px;">
        ${companyInfo.logoBase64 ? `<img src="${companyInfo.logoBase64}" style="width: 80px; height: 60px; margin-bottom: 10px;" alt="Logo">` : ''}
        <div style="font-size: 24px; font-weight: bold; color: #2C5F6F;">${isRTL ? companyInfo.companyNameAr : companyInfo.companyName}</div>
        <div style="font-size: 11px; color: #666; margin-top: 3px;">${companyInfo.address || ''}</div>
        <div style="font-size: 11px; color: #666;">${companyInfo.phone || ''} | ${companyInfo.email || ''}</div>
      </div>

      <!-- Report Title -->
      <div style="background: #4A90A4; color: white; padding: 8px; text-align: center; font-size: 18px; font-weight: bold; margin: 15px 0;">
        ${t.title} - ${reportData.payrollNumber}
      </div>
      <div style="text-align: center; margin-bottom: 15px; font-size: 11px; color: #666;">
        ${reportData.type === 'Branch' && reportData.branchName ? `${t.branch}: ${isRTL ? reportData.branchNameAr : reportData.branchName} | ` : ''}${t.period}: ${new Date(reportData.periodStart).toLocaleDateString('en-GB')} → ${new Date(reportData.periodEnd).toLocaleDateString('en-GB')}
      </div>

      <!-- Summary Cards -->
      <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin: 15px 0;">
        <div style="border: 2px solid #81C784; padding: 10px; text-align: center; border-radius: 5px;">
          <div style="font-size: 11px; color: #666; margin-bottom: 5px;">${t.totalEmployees}</div>
          <div style="font-size: 20px; font-weight: bold; color: #388E3C;">${selectedEmployees.length}</div>
        </div>
        <div style="border: 2px solid #4A90A4; padding: 10px; text-align: center; border-radius: 5px;">
          <div style="font-size: 11px; color: #666; margin-bottom: 5px;">${t.totalEarnings}</div>
          <div style="font-size: 20px; font-weight: bold; color: #2C5F6F;">${formatNumber(totalEarnings)}</div>
        </div>
        <div style="border: 2px solid #E57373; padding: 10px; text-align: center; border-radius: 5px;">
          <div style="font-size: 11px; color: #666; margin-bottom: 5px;">${t.totalDeductions}</div>
          <div style="font-size: 20px; font-weight: bold; color: #C62828;">${formatNumber(totalDeductions)}</div>
        </div>
        <div style="border: 2px solid #BA68C8; padding: 10px; text-align: center; border-radius: 5px;">
          <div style="font-size: 11px; color: #666; margin-bottom: 5px;">${t.netSalary}</div>
          <div style="font-size: 20px; font-weight: bold; color: #7B1FA2;">${formatNumber(totalNetSalary)}</div>
        </div>
      </div>

      <!-- Payment Split -->
      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin: 15px 0;">
        <div style="border: 2px solid #64B5F6; padding: 10px; border-radius: 5px;">
          <div style="font-size: 11px; color: #666;">${t.totalBank}</div>
          <div style="font-size: 16px; font-weight: bold; color: #1976D2;">${formatCurrency(totalBank)}</div>
        </div>
        <div style="border: 2px solid #FFB74D; padding: 10px; border-radius: 5px;">
          <div style="font-size: 11px; color: #666;">${t.totalCash}</div>
          <div style="font-size: 16px; font-weight: bold; color: #F57C00;">${formatCurrency(totalCash)}</div>
        </div>
      </div>

      <!-- Employees Table -->
      <table style="width: 100%; border-collapse: collapse; margin: 15px 0; font-size: 10px;">
        <thead>
          <tr style="background: #4A90A4; color: white;">
            <th style="padding: 8px 5px; text-align: ${isRTL ? 'right' : 'left'};">${t.employeeCode}</th>
            <th style="padding: 8px 5px; text-align: ${isRTL ? 'right' : 'left'};">${t.employeeName}</th>
            <th style="padding: 8px 5px; text-align: ${isRTL ? 'right' : 'left'};">${t.basicSalary}</th>
            <th style="padding: 8px 5px; text-align: ${isRTL ? 'right' : 'left'};">${t.overtime}</th>
            <th style="padding: 8px 5px; text-align: ${isRTL ? 'right' : 'left'};">${t.totalEarnings}</th>
            <th style="padding: 8px 5px; text-align: ${isRTL ? 'right' : 'left'};">${t.absences}</th>
            <th style="padding: 8px 5px; text-align: ${isRTL ? 'right' : 'left'};">${t.penalties}</th>
            <th style="padding: 8px 5px; text-align: ${isRTL ? 'right' : 'left'};">${t.totalDeductions}</th>
            <th style="padding: 8px 5px; text-align: ${isRTL ? 'right' : 'left'};">${t.netSalary}</th>
          </tr>
        </thead>
        <tbody>
          ${selectedEmployees.map((emp: any, idx: number) => `
            <tr style="background: ${idx % 2 === 0 ? '#FFFFFF' : '#F5F5F5'};">
              <td style="padding: 6px 5px; border-bottom: 1px solid #E0E0E0; text-align: ${isRTL ? 'right' : 'left'}; color: #000;">${emp.employeeCode}</td>
              <td style="padding: 6px 5px; border-bottom: 1px solid #E0E0E0; text-align: ${isRTL ? 'right' : 'left'}; color: #000;">${isRTL ? emp.employeeNameAr : emp.employeeName}</td>
              <td style="padding: 6px 5px; border-bottom: 1px solid #E0E0E0; text-align: ${isRTL ? 'right' : 'left'}; color: #000;">${formatNumber(emp.basicSalary)}</td>
              <td style="padding: 6px 5px; border-bottom: 1px solid #E0E0E0; text-align: ${isRTL ? 'right' : 'left'}; color: #000;">${formatNumber(emp.overtimePay)}</td>
              <td style="padding: 6px 5px; border-bottom: 1px solid #E0E0E0; text-align: ${isRTL ? 'right' : 'left'}; font-weight: bold; color: #000;">${formatNumber(emp.totalEarnings)}</td>
              <td style="padding: 6px 5px; border-bottom: 1px solid #E0E0E0; text-align: ${isRTL ? 'right' : 'left'}; color: #000;">${formatNumber(emp.absencesAmount)}</td>
              <td style="padding: 6px 5px; border-bottom: 1px solid #E0E0E0; text-align: ${isRTL ? 'right' : 'left'}; color: #000;">${formatNumber(emp.penaltiesAmount)}</td>
              <td style="padding: 6px 5px; border-bottom: 1px solid #E0E0E0; text-align: ${isRTL ? 'right' : 'left'}; font-weight: bold; color: #000;">${formatNumber(emp.totalDeductions)}</td>
              <td style="padding: 6px 5px; border-bottom: 1px solid #E0E0E0; text-align: ${isRTL ? 'right' : 'left'}; font-weight: bold; color: #7B1FA2;">${formatNumber(emp.netSalary)}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>

      <!-- Signatures -->
      <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 20px; margin-top: 40px;">
        <div style="text-align: center; padding: 10px; border-top: 2px solid #333;">
          <div style="height: 50px;"></div>
          <div style="font-weight: bold; margin-top: 5px; color: #2C5F6F; font-size: 12px;">${t.financialManager}</div>
        </div>
        <div style="text-align: center; padding: 10px; border-top: 2px solid #333;">
          <div style="height: 50px;"></div>
          <div style="font-weight: bold; margin-top: 5px; color: #2C5F6F; font-size: 12px;">${t.hrManager}</div>
        </div>
        <div style="text-align: center; padding: 10px; border-top: 2px solid #333;">
          <div style="height: 50px;"></div>
          <div style="font-weight: bold; margin-top: 5px; color: #2C5F6F; font-size: 12px;">${t.boardChairman}</div>
        </div>
      </div>
    `;

    document.body.appendChild(tempDiv);

    try {
      const canvas = await html2canvas(tempDiv, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pdfWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;

      // Add first page
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;

      // Add additional pages if needed
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pdfHeight;
      }

      pdf.save(`Payroll_Report_${reportData.payrollNumber}.pdf`);
      
      document.body.removeChild(tempDiv);
      toast.success(t.exportPdf + ' ✓');
    } catch (error) {
      console.error('PDF export failed:', error);
      toast.error(isRTL ? 'فشل تصدير PDF' : 'PDF export failed');
      document.body.removeChild(tempDiv);
    }
  };

  const printEmployeeSlip = async (employee: any) => {
    if (!reportData || !companyInfo) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const slipHTML = `
      <!DOCTYPE html>
      <html dir="${isRTL ? 'rtl' : 'ltr'}">
      <head>
        <meta charset="UTF-8">
        <title>${t.payslip} - ${isRTL ? employee.employeeNameAr : employee.employeeName}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: Arial, sans-serif; 
            padding: 20mm; 
            background: white;
            direction: ${isRTL ? 'rtl' : 'ltr'};
          }
          .slip-container { 
            max-width: 210mm; 
            margin: 0 auto; 
            border: 2px solid #00796B;
            padding: 15mm;
          }
          .header { 
            text-align: center; 
            border-bottom: 3px solid #00796B; 
            padding-bottom: 10px; 
            margin-bottom: 15px;
          }
          .logo { width: 80px; height: 60px; margin-bottom: 10px; }
          .company-name { font-size: 24px; font-weight: bold; color: #00796B; margin-bottom: 5px; }
          .company-info { font-size: 12px; color: #666; }
          .slip-title { 
            background: #00796B; 
            color: white; 
            padding: 8px; 
            text-align: center; 
            font-size: 18px; 
            font-weight: bold;
            margin: 15px 0;
          }
          .info-section { 
            display: grid; 
            grid-template-columns: 1fr 1fr; 
            gap: 10px; 
            margin-bottom: 15px;
            background: #F5F5F5;
            padding: 10px;
            border-radius: 5px;
          }
          .info-row { display: flex; justify-content: space-between; padding: 5px 0; }
          .info-label { font-weight: bold; color: #00796B; }
          .info-value { color: #333; }
          .details-table { 
            width: 100%; 
            border-collapse: collapse; 
            margin: 15px 0;
          }
          .details-table th {
            background: #00796B;
            color: white;
            padding: 10px;
            text-align: ${isRTL ? 'right' : 'left'};
            font-size: 14px;
          }
          .details-table td {
            padding: 8px;
            border-bottom: 1px solid #ddd;
            text-align: ${isRTL ? 'right' : 'left'};
            color: #333;
          }
          .details-table tr:nth-child(even) { background: #F9F9F9; }
          .total-row {
            background: #00796B !important;
            font-weight: bold;
            font-size: 16px;
            color: white;
          }
          .total-row td { color: white !important; }
          .net-salary { 
            background: #00796B; 
            color: white; 
            padding: 15px; 
            text-align: center; 
            font-size: 20px; 
            font-weight: bold;
            margin: 20px 0;
            border-radius: 5px;
          }
          .signatures { 
            display: grid; 
            grid-template-columns: 1fr 1fr 1fr; 
            gap: 20px; 
            margin-top: 40px;
            page-break-inside: avoid;
          }
          .signature-box { 
            text-align: center; 
            padding: 10px;
            border-top: 2px solid #333;
          }
          .signature-label { 
            font-weight: bold; 
            margin-top: 5px; 
            color: #00796B;
            font-size: 14px;
          }
          .signature-space { height: 50px; }
          @media print {
            body { padding: 10mm; }
            .slip-container { border: none; page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <div class="slip-container">
          <!-- Header -->
          <div class="header">
            ${companyInfo.logoBase64 ? `<img src="${companyInfo.logoBase64}" class="logo" alt="Logo">` : ''}
            <div class="company-name">${isRTL ? companyInfo.companyNameAr : companyInfo.companyName}</div>
            <div class="company-info">${companyInfo.address || ''}</div>
            <div class="company-info">${companyInfo.phone || ''} | ${companyInfo.email || ''}</div>
          </div>

          <!-- Slip Title -->
          <div class="slip-title">${t.payslip} - ${reportData.payrollNumber}${reportData.type === 'Branch' && reportData.branchName ? ` (${isRTL ? reportData.branchNameAr : reportData.branchName})` : ''}</div>

          <!-- Employee Info -->
          <div class="info-section">
            <div class="info-row">
              <span class="info-label">${t.employeeCode}:</span>
              <span class="info-value">${employee.employeeCode}</span>
            </div>
            <div class="info-row">
              <span class="info-label">${t.employeeName}:</span>
              <span class="info-value">${isRTL ? employee.employeeNameAr : employee.employeeName}</span>
            </div>
            <div class="info-row">
              <span class="info-label">${t.period}:</span>
              <span class="info-value">${new Date(reportData.periodStart).toLocaleDateString('en-GB')} - ${new Date(reportData.periodEnd).toLocaleDateString('en-GB')}</span>
            </div>
            <div class="info-row">
              <span class="info-label">${t.payrollDate}:</span>
              <span class="info-value">${new Date().toLocaleDateString('en-GB')}</span>
            </div>
          </div>

          <!-- Earnings Table -->
          <table class="details-table">
            <thead>
              <tr>
                <th>${t.earnings}</th>
                <th style="text-align: right;">${t.amount}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>${t.basicSalary}</td>
                <td style="text-align: right;">${formatCurrency(employee.basicSalary)}</td>
              </tr>
              <tr>
                <td>${t.housing}</td>
                <td style="text-align: right;">${formatCurrency(employee.housingAllowance)}</td>
              </tr>
              <tr>
                <td>${t.transport}</td>
                <td style="text-align: right;">${formatCurrency(employee.transportAllowance)}</td>
              </tr>
              <tr>
                <td>${t.overtime}</td>
                <td style="text-align: right;">${formatCurrency(employee.overtimePay)}</td>
              </tr>
              <tr>
                <td>${t.offDay}</td>
                <td style="text-align: right;">${formatCurrency(employee.offDayPay || 0)}</td>
              </tr>
              <tr>
                <td>${t.holiday}</td>
                <td style="text-align: right;">${formatCurrency(employee.publicHolidayPay)}</td>
              </tr>
              <tr class="total-row">
                <td>${t.totalEarnings}</td>
                <td style="text-align: right;">${formatCurrency(employee.totalEarnings)}</td>
              </tr>
            </tbody>
          </table>

          <!-- Deductions Table -->
          <table class="details-table">
            <thead>
              <tr>
                <th>${t.deductions}</th>
                <th style="text-align: right;">${t.amount}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>${t.absences}</td>
                <td style="text-align: right;">${formatCurrency(employee.absencesAmount)}</td>
              </tr>
              <tr>
                <td>${t.penalties}</td>
                <td style="text-align: right;">${formatCurrency(employee.penaltiesAmount)}</td>
              </tr>
              <tr class="total-row">
                <td>${t.totalDeductions}</td>
                <td style="text-align: right;">${formatCurrency(employee.totalDeductions)}</td>
              </tr>
            </tbody>
          </table>

          <!-- Net Salary -->
          <div class="net-salary">
            ${t.netSalary}: ${formatCurrency(employee.netSalary)}
          </div>

          <!-- Payment Breakdown -->
          <table class="details-table" style="margin-bottom:15px;">
            <thead>
              <tr>
                <th>${isRTL ? 'طريقة الدفع' : 'Payment Method'}</th>
                <th style="text-align: right;">${t.amount}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>${isRTL ? 'تحويل بنكي' : 'Bank Transfer'}</td>
                <td style="text-align: right; font-weight: bold; color: #1976d2;">${formatCurrency(employee.bankAmount || 0)}</td>
              </tr>
              <tr>
                <td>${isRTL ? 'نقدي (كاش)' : 'Cash'}</td>
                <td style="text-align: right; font-weight: bold; color: #e65100;">${formatCurrency(employee.cashAmount || 0)}</td>
              </tr>
            </tbody>
          </table>

          <!-- Signatures -->
          <div class="signatures">
            <div class="signature-box">
              <div class="signature-space"></div>
              <div class="signature-label">${isRTL ? 'توقيع الموظف' : 'Employee Signature'}</div>
            </div>
            <div class="signature-box">
              <div class="signature-space"></div>
              <div class="signature-label">${isRTL ? 'توقيع المدير المباشر' : 'Direct Manager Signature'}</div>
            </div>
            <div class="signature-box">
              <div class="signature-space"></div>
              <div class="signature-label">${isRTL ? 'توقيع مدير شؤون الموظفÙŠÙ†' : 'HR Manager Signature'}</div>
            </div>
          </div>
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(slipHTML);
    printWindow.document.close();
    
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
    };
  };

  const printAllEmployeeSlips = async () => {
    if (!reportData || !companyInfo) return;

    // Open employee selection dialog for slips — pre-select based on payment filter.
    // Default behavior for slips: when no explicit filter is set, restrict to CASH employees
    // (slips are typically printed for cash distribution; bank-paid employees receive bank transfer).
    const baseList = applyPaymentFilter(reportData.employees);
    const eligible = printPaymentFilter === '' ? baseList.filter((e: any) => (e.cashAmount || 0) > 0) : baseList;
    setEmployeeSelectionDialog('slips');
    setSelectedEmployeeIds(new Set(eligible.map((emp: any) => emp.employeeId)));
  };

  const printSelectedSlips = async () => {
    if (!reportData || !companyInfo) return;

    const selectedEmployees = reportData.employees.filter((emp: any) => 
      selectedEmployeeIds.has(emp.employeeId)
    );

    if (selectedEmployees.length === 0) {
      toast.error(isRTL ? 'الرجاء اختيار موظف واحد على الأقل' : 'Please select at least one employee');
      return;
    }

    setEmployeeSelectionDialog('none');
    toast.info(isRTL ? `جاري طباعة ${selectedEmployees.length} سليب...` : `Printing ${selectedEmployees.length} slips...`);

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    // Build HTML for ALL selected employees - each in a separate page
    const allSlipsHTML = `
      <!DOCTYPE html>
      <html dir="${isRTL ? 'rtl' : 'ltr'}">
      <head>
        <meta charset="UTF-8">
        <title>${t.payslip} - ${reportData.payrollNumber}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: Arial, sans-serif; 
            background: white;
            direction: ${isRTL ? 'rtl' : 'ltr'};
          }
          .slip-page {
            padding: 20mm; 
            page-break-after: always;
          }
          .slip-page:last-child {
            page-break-after: auto;
          }
          .slip-container { 
            max-width: 210mm; 
            margin: 0 auto; 
            border: 2px solid #00796B;
            padding: 15mm;
          }
          .header { 
            text-align: center; 
            border-bottom: 3px solid #00796B; 
            padding-bottom: 10px; 
            margin-bottom: 15px;
          }
          .logo { width: 80px; height: 60px; margin-bottom: 10px; }
          .company-name { font-size: 24px; font-weight: bold; color: #00796B; margin-bottom: 5px; }
          .company-info { font-size: 12px; color: #666; }
          .slip-title { 
            background: #00796B; 
            color: white; 
            padding: 8px; 
            text-align: center; 
            font-size: 18px; 
            font-weight: bold;
            margin: 15px 0;
          }
          .info-section { 
            display: grid; 
            grid-template-columns: 1fr 1fr; 
            gap: 10px; 
            margin-bottom: 15px;
            background: #F5F5F5;
            padding: 10px;
            border-radius: 5px;
          }
          .info-row { display: flex; justify-content: space-between; padding: 5px 0; }
          .info-label { font-weight: bold; color: #00796B; }
          .info-value { color: #333; }
          .details-table { 
            width: 100%; 
            border-collapse: collapse; 
            margin: 15px 0;
          }
          .details-table th {
            background: #00796B;
            color: white;
            padding: 10px;
            text-align: ${isRTL ? 'right' : 'left'};
            font-size: 14px;
          }
          .details-table td {
            padding: 8px;
            border-bottom: 1px solid #ddd;
            text-align: ${isRTL ? 'right' : 'left'};
            color: #333;
          }
          .details-table tr:nth-child(even) { background: #F9F9F9; }
          .total-row {
            background: #00796B !important;
            font-weight: bold;
            font-size: 16px;
            color: white;
          }
          .total-row td { color: white !important; }
          .net-salary { 
            background: #00796B; 
            color: white; 
            padding: 15px; 
            text-align: center; 
            font-size: 20px; 
            font-weight: bold;
            margin: 20px 0;
            border-radius: 5px;
          }
          .signatures { 
            display: grid; 
            grid-template-columns: 1fr 1fr 1fr; 
            gap: 20px; 
            margin-top: 40px;
          }
          .signature-box { 
            text-align: center; 
            padding: 10px;
            border-top: 2px solid #333;
          }
          .signature-label { 
            font-weight: bold; 
            margin-top: 5px; 
            color: #00796B;
            font-size: 14px;
          }
          .signature-space { height: 50px; }
          @media print {
            .slip-page { padding: 10mm; }
            .slip-container { border: none; }
          }
        </style>
      </head>
      <body>
        ${selectedEmployees.map((employee: any) => `
          <div class="slip-page">
            <div class="slip-container">
              <!-- Header -->
              <div class="header">
                ${companyInfo.logoBase64 ? `<img src="${companyInfo.logoBase64}" class="logo" alt="Logo">` : ''}
                <div class="company-name">${isRTL ? companyInfo.companyNameAr : companyInfo.companyName}</div>
                <div class="company-info">${companyInfo.address || ''}</div>
                <div class="company-info">${companyInfo.phone || ''} | ${companyInfo.email || ''}</div>
              </div>

              <!-- Slip Title -->
              <div class="slip-title">${t.payslip} - ${reportData.payrollNumber}${reportData.type === 'Branch' && reportData.branchName ? ` (${isRTL ? reportData.branchNameAr : reportData.branchName})` : ''}</div>

              <!-- Employee Info -->
              <div class="info-section">
                <div class="info-row">
                  <span class="info-label">${t.employeeCode}:</span>
                  <span class="info-value">${employee.employeeCode}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">${t.employeeName}:</span>
                  <span class="info-value">${isRTL ? employee.employeeNameAr : employee.employeeName}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">${t.period}:</span>
                  <span class="info-value">${new Date(reportData.periodStart).toLocaleDateString('en-GB')} - ${new Date(reportData.periodEnd).toLocaleDateString('en-GB')}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">${t.payrollDate}:</span>
                  <span class="info-value">${new Date().toLocaleDateString('en-GB')}</span>
                </div>
              </div>

              <!-- Earnings Table -->
              <table class="details-table">
                <thead>
                  <tr>
                    <th>${t.earnings}</th>
                    <th style="text-align: right;">${t.amount}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>${t.basicSalary}</td>
                    <td style="text-align: right;">${formatCurrency(employee.basicSalary)}</td>
                  </tr>
                  <tr>
                    <td>${t.housing}</td>
                    <td style="text-align: right;">${formatCurrency(employee.housingAllowance)}</td>
                  </tr>
                  <tr>
                    <td>${t.transport}</td>
                    <td style="text-align: right;">${formatCurrency(employee.transportAllowance)}</td>
                  </tr>
                  <tr>
                    <td>${t.overtime}</td>
                    <td style="text-align: right;">${formatCurrency(employee.overtimePay)}</td>
                  </tr>
                  <tr>
                    <td>${t.offDay}</td>
                    <td style="text-align: right;">${formatCurrency(employee.offDayPay || 0)}</td>
                  </tr>
                  <tr>
                    <td>${t.holiday}</td>
                    <td style="text-align: right;">${formatCurrency(employee.publicHolidayPay)}</td>
                  </tr>
                  <tr class="total-row">
                    <td>${t.totalEarnings}</td>
                    <td style="text-align: right;">${formatCurrency(employee.totalEarnings)}</td>
                  </tr>
                </tbody>
              </table>

              <!-- Deductions Table -->
              <table class="details-table">
                <thead>
                  <tr>
                    <th>${t.deductions}</th>
                    <th style="text-align: right;">${t.amount}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>${t.absences}</td>
                    <td style="text-align: right;">${formatCurrency(employee.absencesAmount)}</td>
                  </tr>
                  <tr>
                    <td>${t.penalties}</td>
                    <td style="text-align: right;">${formatCurrency(employee.penaltiesAmount)}</td>
                  </tr>
                  <tr class="total-row">
                    <td>${t.totalDeductions}</td>
                    <td style="text-align: right;">${formatCurrency(employee.totalDeductions)}</td>
                  </tr>
                </tbody>
              </table>

              <!-- Net Salary -->
              <div class="net-salary">
                ${t.netSalary}: ${formatCurrency(employee.netSalary)}
              </div>

<!-- Payment Breakdown -->              <table class="details-table" style="margin-bottom:15px;">                <thead><tr><th>${isRTL ? 'طريقة الدفع' : 'Payment Method'}</th><th style="text-align:right;">${t.amount}</th></tr></thead>                <tbody>                  <tr><td>${isRTL ? 'تحويل بنكي' : 'Bank Transfer'}</td><td style="text-align:right;font-weight:bold;color:#1976d2;">${formatCurrency(employee.bankAmount || 0)}</td></tr>                  <tr><td>${isRTL ? 'نقدي (كاش)' : 'Cash'}</td><td style="text-align:right;font-weight:bold;color:#e65100;">${formatCurrency(employee.cashAmount || 0)}</td></tr>                </tbody>              </table>
              <!-- Signatures -->
              <div class="signatures">
                <div class="signature-box">
                  <div class="signature-space"></div>
                  <div class="signature-label">${isRTL ? 'توقيع الموظف' : 'Employee Signature'}</div>
                </div>
                <div class="signature-box">
                  <div class="signature-space"></div>
                  <div class="signature-label">${isRTL ? 'توقيع المدير المباشر' : 'Direct Manager Signature'}</div>
                </div>
                <div class="signature-box">
                  <div class="signature-space"></div>
                  <div class="signature-label">${isRTL ? 'توقيع مدير شؤون الموظفÙŠÙ†' : 'HR Manager Signature'}</div>
                </div>
              </div>
            </div>
          </div>
        `).join('')}
      </body>
      </html>
    `;

    printWindow.document.write(allSlipsHTML);
    printWindow.document.close();
    
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
    };
  };

  const handleEmployeeToggle = (employeeId: number) => {
    const newSelected = new Set(selectedEmployeeIds);
    if (newSelected.has(employeeId)) {
      newSelected.delete(employeeId);
    } else {
      newSelected.add(employeeId);
    }
    setSelectedEmployeeIds(newSelected);
  };

  const handleSelectAll = () => {
    if (!reportData) return;
    setSelectedEmployeeIds(new Set(reportData.employees.map((emp: any) => emp.employeeId)));
  };

  const handleDeselectAll = () => {
    setSelectedEmployeeIds(new Set());
  };

  const exportBurganBankWorkers = async () => {
    if (payrollTypeFilter !== 'Worker') {
      toast.warning(isRTL 
        ? '⚠️ يرجى اختيار "عمال" من نوع الموظفين لتصدير ملف العمال' 
        : '⚠️ Please select "Workers" to export workers file'
      );
      return;
    }

    // استخدام الشهر والسنة من Monthly View
    setBurganMonth(monthlyViewMonth);
    setBurganYear(monthlyViewYear);
    setBurganDateDialog(true);
  };

  const handleBurganExport = async () => {
    toast.info(isRTL ? 'جاري جلب بيانات جميع العمال...' : 'Fetching all workers data...');

    try {
      // جلب جميع الرواتب
      const allPayrolls = await payrollsService.getAll();
      
      
      // تصفية الرواتب للشهر والسنة المختارة
      const periodsPayrolls = allPayrolls.filter((p: any) => {
        // استخدام UTC لتجنب مشاكل المنطقة الزمنية
        const pEnd = new Date(p.periodEnd);
        const pMonth = pEnd.getUTCMonth() + 1;
        const pYear = pEnd.getUTCFullYear();
        return pMonth === burganMonth && pYear === burganYear;
      });


      if (periodsPayrolls.length === 0) {
        toast.error(isRTL ? 'لم يتم العثور على رواتب في هذه الفترة' : 'No payrolls found for this period');
        setBurganDateDialog(false);
        return;
      }

      // جمع جميع الموظفÙŠÙ† من كل الرواتب (كل الفروع)
      let allWorkers: any[] = [];
      
      for (const payroll of periodsPayrolls) {
        // جلب تفاصيل كل راتب
        const payrollReport = await payrollsService.getReport(payroll.payrollId);
        if (payrollReport && payrollReport.employees && Array.isArray(payrollReport.employees)) {
          allWorkers = allWorkers.concat(payrollReport.employees);
        }
      }


      // تصفية: BankFileType='Worker' + حساب موجود + مبلغ فعلي للبنك > 0
      // ✅ FIX: نستخدم bankAmount (المبلغ المرسل للبنك) وليس netSalary
      // (صافي الراتب) — لأن موظفين مثل Hamad Ibrahim Siefelnasr يحصلون على
      // راتب صافٍ 264 د.ك لكن كله كاش (bankAmount=0)، فإذا استخدمنا
      // netSalary يظهرون في ملف البنك بمبلغ صفر والبنك يرفض الملف كله.
      const workersData = allWorkers.filter((emp: any) =>
        emp.bankFileType === 'Worker' &&
        emp.bankAccountNumber &&
        emp.bankAccountNumber.trim() !== '' &&
        emp.bankAmount > 0
      );


      if (workersData.length === 0) {
        toast.error(isRTL ? 'لا يوجد عمال بحسابات بنكية في هذه الفترة' : 'No workers with bank accounts found in this period');
        setBurganDateDialog(false);
        return;
      }

      toast.info(isRTL ? `جاري تصدير ${workersData.length} عامل من جميع الفروع...` : `Exporting ${workersData.length} workers from all branches...`);

      // التحقق من وجود اسم الشركة
      if (!payrollCompanyName) {
        toast.error(isRTL ? 'اسم الشركة غير موجود في الإعدادات. يرجى تكوينه أولاً.' : 'Company name not found in settings. Please configure it first.');
        setLoading(false);
        return;
      }

      // معلومات الشركة - جلب من إعدادات الرواتب
      const companyCode = "00000";
      const companyName = companyInfo?.companyName || payrollCompanyName || 'Company'; // من بيانات الشركة

      // استخدام الشهر والسنة المختارة من Dialog
      const month = burganMonth;
      const year = burganYear;
      const monthYear = `${month.toString().padStart(2, '0')}${year}`; // MMYYYY

      const lines: string[] = [];

      // ═══════════════════════════════════════════════════════
      // السطر 1: HEADER (93 حرف)
      // ═══════════════════════════════════════════════════════
      // Format: CompanyCode(5) + CompanyName(45) + MonthYear(6) + Spaces(37)
      const header = 
        companyCode.padEnd(5, '0').substring(0, 5) +            // [1-5] Company Code (00000)
        companyName.padEnd(45, ' ').substring(0, 45) +          // [6-50] Company Name (45)
        monthYear.padEnd(6, ' ').substring(0, 6) +              // [51-56] Month/Year (MMYYYY)
        "".padEnd(37, ' ');                                      // [57-93] Spaces (37)
      
      lines.push(header);

      // ═══════════════════════════════════════════════════════
      // السطر 2+: EMPLOYEE RECORDS (93 حرف لكل موظف)
      // ═══════════════════════════════════════════════════════
      let serialNumber = 1;
      let totalAmount = 0;

      workersData.forEach((emp: any) => {
        // استخدام BankCardName إذا موجود، وإلا FullName
        const employeeName = (emp.bankCardName && emp.bankCardName.trim() !== '' 
          ? emp.bankCardName 
          : emp.employeeName || emp.employeeNameAr || '')
          .padEnd(40, ' ')
          .substring(0, 40);

        // رقم الحساب البنكÙŠ (16 رقم) - تنظيف من المسافات والرموز
        const accountNumber = (emp.bankAccountNumber || '')
          .replace(/\s/g, '')           // إزالة المسافات
          .replace(/\-/g, '')           // إزالة الشرطات
          .padEnd(16, '0')              // تعبئة بأصفار إذا أقل من 16
          .substring(0, 16);            // أخذ أول 16 رقم فقط

        // المبلغ بالفلس (× 1000) - 13 رقم
        const amountFils = Math.floor(emp.bankAmount * 1000)
          .toString()
          .padStart(13, '0');

        // رقم ملف الشركة (12 رقم) - استخدام القيمة من الموظف أو default
        const companyFileNumber = (emp.companyFileNumber || '000100039539')
          .replace(/\s/g, '')
          .padStart(12, '0')
          .substring(0, 12);

        // Format: Type(1) + Serial(11) + Name(40) + Account(16) + Amount(13) + CompanyFile(12)
        const employeeLine = 
          "1" +                                               // [1] Type
          serialNumber.toString().padStart(11, '0') +         // [2-12] Serial Number
          employeeName +                                      // [13-52] Employee Name (40)
          accountNumber +                                     // [53-68] Bank Account (16)
          amountFils +                                        // [69-81] Amount in Fils (13)
          companyFileNumber;                                  // [82-93] Company File Number (12)

        lines.push(employeeLine);
        
        serialNumber++;
        totalAmount += emp.bankAmount;
      });

      // ═══════════════════════════════════════════════════════
      // السطر الأخير: FOOTER (93 حرف)
      // ═══════════════════════════════════════════════════════
      // Format: Type(1) + Count(5) + TotalAmount(13) + Space(1) + MysteryField(40) + Spaces(33)
      const recordCount = workersData.length
        .toString()
        .padStart(5, '0');

      const totalAmountFils = Math.floor(totalAmount * 1000)
        .toString()
        .padStart(13, '0');

      // Calculate Mystery Field: Sum(Mid([CARDNO],12)*[Amt])
      // Mid([CARDNO],12) in VBA = substring from position 11 in JavaScript (0-based)
      let mysterySum = 0;
      workersData.forEach((emp: any) => {
        const accountNumber = (emp.bankAccountNumber || '').replace(/\s/g, '').replace(/\-/g, '');
        if (accountNumber.length >= 12) {
          // Get last digits starting from position 11 (12th character in VBA)
          const lastDigits = accountNumber.substring(11);
          const numericValue = parseFloat(lastDigits) || 0;
          mysterySum += numericValue * emp.bankAmount;
        }
      });

      // Format mystery field: 40 chars total (37 before decimal + . + 3 after decimal)
      const mysteryField = mysterySum.toFixed(3).padStart(40, '0');

      const footer = 
        "2" +                                                   // [1] Type
        recordCount +                                           // [2-6] Employee Count (5)
        totalAmountFils +                                       // [7-19] Total Amount in Fils (13)
        " " +                                                   // [20] Space
        mysteryField +                                          // [21-60] Mystery Field (40)
        "".padEnd(33, ' ');                                     // [61-93] Spaces (33)

      lines.push(footer);

      // ═══════════════════════════════════════════════════════
      // تحويل إلى ملف TXT وتحميله (Windows format CRLF)
      // ═══════════════════════════════════════════════════════
      const fileContent = lines.join('\r\n');  // Windows Line Ending (CRLF)
      const fileName = `bdinar_format.txt`;  // اسم ثابت
      
      const blob = new Blob([fileContent], { type: 'text/plain;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // إغلاق Dialog
      setBurganDateDialog(false);

      toast.success(isRTL 
        ? `✓ تم التصدير: ${workersData.length} عامل من جميع الفروع | ${totalAmount.toFixed(3)} د.ك | ${month.toString().padStart(2, '0')}/${year}`
        : `✓ Exported: ${workersData.length} workers from all branches | ${totalAmount.toFixed(3)} KWD | ${month.toString().padStart(2, '0')}/${year}`
      );
    } catch (error) {
      console.error('Bank file export failed:', error);
      toast.error(isRTL ? 'فشل تصدير ملف البنك' : 'Bank file export failed');
      setBurganDateDialog(false);
    }
  };

  const exportStaffPayroll = async () => {
    if (payrollTypeFilter !== 'Regular') {
      toast.warning(isRTL 
        ? '⚠️ يرجى اختيار "موظفين إداريين" من نوع الموظفين لتصدير ملف الموظفين' 
        : '⚠️ Please select "Regular Staff" to export staff file'
      );
      return;
    }

    // استخدام الشهر والسنة من Monthly View
    setStaffMonth(monthlyViewMonth);
    setStaffYear(monthlyViewYear);
    setStaffDateDialog(true);
  };

  const handleStaffExport = async () => {
    try {
      toast.info(isRTL ? 'جاري جلب بيانات الموظفين الإداريين...' : 'Fetching regular staff data...');

      const allPayrolls = await payrollsService.getAll();
      
      // تصفية الرواتب للشهر والسنة المختارة
      const periodsPayrolls = allPayrolls.filter((p: any) => {
        // استخدام UTC لتجنب مشاكل المنطقة الزمنية
        const pEnd = new Date(p.periodEnd);
        const pMonth = pEnd.getUTCMonth() + 1;
        const pYear = pEnd.getUTCFullYear();
        return pMonth === staffMonth && pYear === staffYear;
      });

      if (periodsPayrolls.length === 0) {
        toast.error(isRTL ? 'لم يتم العثور على رواتب في هذه الفترة' : 'No payrolls found for this period');
        setStaffDateDialog(false);
        return;
      }

      let allStaff: any[] = [];
      
      for (const payroll of periodsPayrolls) {
        const payrollReport = await payrollsService.getReport(payroll.payrollId);
        if (payrollReport && payrollReport.employees && Array.isArray(payrollReport.employees)) {
          allStaff = allStaff.concat(payrollReport.employees);
        }
      }

      // تصفية: BankFileType='Regular' + IBAN موجود + راتب > 0
      const staffData = allStaff.filter((emp: any) => {
        const isRegular = emp.bankFileType === 'Regular';
        const hasIban = emp.iban && emp.iban.trim() !== '';
        const hasSalary = emp.bankAmount > 0;
        
        return isRegular && hasIban && hasSalary;
      });

      if (staffData.length === 0) {
        toast.error(isRTL ? 'لا يوجد موظفين إداريين بـ IBAN في هذه الفترة' : 'No regular staff with IBAN found in this period');
        setStaffDateDialog(false);
        return;
      }

      toast.info(isRTL ? `جاري تصدير ${staffData.length} موظف إداري من جميع الفروع...` : `Exporting ${staffData.length} regular staff from all branches...`);

      // التحقق من وجود اسم الشركة
      if (!payrollCompanyName) {
        toast.error(isRTL ? 'اسم الشركة غير موجود في الإعدادات. يرجى تكوينه أولاً.' : 'Company name not found in settings. Please configure it first.');
        setLoading(false);
        return;
      }

      // معلومات الشركة - من SystemSettings
      const companyName = companyInfo?.companyName || payrollCompanyName || 'Company'; // من بيانات الشركة
      const coCode = "0000000000"; // كود الشركة 10 خانات - يمكن تعديله
      
      // استخدام الشهر والسنة المختارة من Dialog
      const month = staffMonth;
      const year = staffYear;
      
      // تاريخ بصيغة DDMMYY
      const day = "01"; // أول يوم من الشهر
      const dateString = `${day}${month.toString().padStart(2, '0')}${String(year).substring(2)}`;

      const lines: string[] = [];

      // ═══════════════════════════════════════════════════════
      // السطر 1: HEADER (125 حرف)
      // ═══════════════════════════════════════════════════════
      // Format: HDR + space + DDMMYY + space + 008 + space + LOCAL_BANK_CODE + spaces + CBKS
      const header = 
        "HDR " +
        dateString + " " +
        "008 " +
        "008 " +
        companyName.padEnd(101, ' ');
      
      lines.push(header);

      // ═══════════════════════════════════════════════════════
      // السطر 2+: EMPLOYEE RECORDS (125 حرف لكل موظف)
      // ═══════════════════════════════════════════════════════
      let serialNumber = 1;
      let totalAmount = 0;
      let mysterySum = 0;

      staffData.forEach((emp: any) => {
        // الاسم (40 حرف)
        const employeeName = (emp.bankCardName && emp.bankCardName.trim() !== '' 
          ? emp.bankCardName 
          : emp.employeeName || emp.employeeNameAr || '')
          .padEnd(40, ' ')
          .substring(0, 40);

        // IBAN (30 حرف) - استخدام IBAN الحقيقي من قاعدة البيانات
        const iban = (emp.iban || '')
          .replace(/\s/g, '')
          .toUpperCase()
          .padEnd(30, '0')
          .substring(0, 30);

        // المبلغ بصيغة 00000000.000 (12 حرف: 8 أرقام + نقطة + 3)
        const amountStr = emp.bankAmount.toFixed(3);
        const amount = amountStr.padStart(12, '0').substring(0, 12);

        // استخراج BankCode و MOSAL_ID من CompanyFileNumber
        // التنسيق: 000100008304-007
        let extBankCode = "000";
        let mosalId = "000000000000";
        
        if (emp.companyFileNumber && emp.companyFileNumber.includes('-')) {
          const parts = emp.companyFileNumber.split('-');
          mosalId = parts[0].padStart(12, '0').substring(0, 12);
          extBankCode = parts[1].padStart(3, '0').substring(0, 3);
        }

        // Format: DTL(3) + Serial(15) + Name(40) + IBAN(30) + Amount(12) + CO_CODE(10) + BankCode(3) + MOSAL_ID(12)
        // Total = 3 + 15 + 40 + 30 + 12 + 10 + 3 + 12 = 125
        const employeeLine = 
          "DTL" +
          serialNumber.toString().padStart(15, '0') +
          employeeName +
          iban +
          amount +
          coCode +
          extBankCode +
          mosalId;

        lines.push(employeeLine);
        
        serialNumber++;
        totalAmount += emp.bankAmount;
        
        // حساب Mystery Field: آخر 5 أرقام من IBAN × المبلغ
        if (iban.length >= 26) {
          const last5 = iban.substring(25);
          const num = parseFloat(last5) || 0;
          mysterySum += num * emp.bankAmount;
        }
      });

      // ═══════════════════════════════════════════════════════
      // السطر الأخير: TRAILER (75 حرف)
      // ═══════════════════════════════════════════════════════
      const recordCount = staffData.length
        .toString()
        .padStart(8, '0');

      const mysteryField = mysterySum.toFixed(3).padStart(34, '0');

      const trailer = 
        "TRL " +
        recordCount + " " +
        mysteryField +
        "".padEnd(28, ' ');

      lines.push(trailer);

      // تحويل إلى ملف TXT وتحميله
      const fileContent = lines.join('\r\n');
      const fileName = `bbformat.txt`;
      
      const blob = new Blob([fileContent], { type: 'text/plain;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setStaffDateDialog(false);

      toast.success(isRTL 
        ? `✓ تم التصدير: ${staffData.length} موظف إداري | ${totalAmount.toFixed(3)} د.ك`
        : `✓ Exported: ${staffData.length} regular staff | ${totalAmount.toFixed(3)} KWD`
      );
    } catch (error) {
      console.error('Staff file export failed:', error);
      toast.error(isRTL ? 'فشل تصدير ملف الموظفين' : 'Staff file export failed');
      setStaffDateDialog(false);
    }
  };


  const handlePrint = () => {
    if (!reportData) return;
    const printWindow = window.open('', '_blank', 'width=1100,height=800');
    if (!printWindow) return;

    const fmt = (n: number) => (n || 0).toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
    const fmtH = (n: number) => (n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const periodStr = `${new Date(reportData.periodStart).toLocaleDateString('en-GB')} → ${new Date(reportData.periodEnd).toLocaleDateString('en-GB')}`;
    const branchLine = reportData.type === 'Branch'
      ? (isRTL ? reportData.branchNameAr || reportData.branchName : reportData.branchName)
      : (isRTL ? 'إدارة' : 'Management');
    // ✅ يطبع فقط الموظفين المعروضين حالياً (مفلترين بنوع الموظف Worker/Staff كما في الجدول)
    // + يطبق فلتر طريقة الدفع المختار (بنك / كاش / الكل)
    const employees = applyPaymentFilter(
      (reportData.employees || []).filter((emp: any) => emp.bankFileType === payrollTypeFilter)
    );
    const totalBankAll = employees.reduce((s: number, e: any) => s + (e.bankAmount || 0), 0);
    const totalCashAll = employees.reduce((s: number, e: any) => s + (e.cashAmount || 0), 0);
    const fEarnings = employees.reduce((s: number, e: any) => s + (e.totalEarnings || 0), 0);
    const fDeductions = employees.reduce((s: number, e: any) => s + (e.totalDeductions || 0), 0);
    const fNet = employees.reduce((s: number, e: any) => s + (e.netSalary || 0), 0);
    const filterLabel = payrollTypeFilter === 'Worker'
      ? (isRTL ? 'عمال' : 'Workers')
      : (isRTL ? 'موظفين إداريين' : 'Staff');

    // عمود الدفع: عرض بنك+نقدي معاً (افتراضي) أو واحد منهما حسب الفلتر
    const showBankCol = printPaymentFilter !== 'cash';
    const showCashCol = printPaymentFilter !== 'bank';
    const paymentColSpan = (showBankCol ? 1 : 0) + (showCashCol ? 1 : 0);

    const renderRow = (emp: any, idx: number) => {
      const allowances = (emp.housingAllowance || 0) + (emp.transportAllowance || 0) + (emp.otherAllowances || 0);
      const dedAbs = emp.absencesAmount || emp.absences || 0;
      const dedPen = emp.penaltiesAmount || emp.penalties || 0;
      const dedOther = emp.otherDeductions || 0;
      const totalDed = dedAbs + dedPen + dedOther;
      return `
      <tr class="emp-row ${idx % 2 === 0 ? 'even' : 'odd'}">
        <td class="cell-code"><strong>${emp.employeeCode}</strong></td>
        <td class="cell-name">${isRTL ? (emp.employeeNameAr || emp.employeeName) : emp.employeeName}</td>
        <td class="cell-num">${fmtH(emp.totalRegularHours || 0)}</td>
        <td class="cell-num">${fmtH(emp.totalOvertimeHours || 0)}</td>
        <td class="cell-num">${fmtH(emp.offDayHours || 0)}</td>
        <td class="cell-num">${fmtH(emp.publicHolidayHours || 0)}</td>
        <td class="cell-amt basic">${fmt(emp.basicSalary || 0)}</td>
        <td class="cell-amt allow">${fmt(allowances)}</td>
        <td class="cell-amt ot">${fmt((emp.overtimePay || 0) + (emp.publicHolidayPay || 0) + (emp.offDayPay || 0))}</td>
        <td class="cell-amt earn"><strong>${fmt(emp.totalEarnings || 0)}</strong></td>
        <td class="cell-amt ded">${fmt(totalDed)}</td>
        <td class="cell-amt net"><strong>${fmt(emp.netSalary || 0)}</strong></td>
        ${showBankCol ? `<td class="cell-amt bank">${fmt(emp.bankAmount || 0)}</td>` : ''}
        ${showCashCol ? `<td class="cell-amt cash">${fmt(emp.cashAmount || 0)}</td>` : ''}
      </tr>`;
    };

    // ✅ تقسيم الموظفين إلى صفحات (يعتمد عليه تكرار الـ Header)
    const ROWS_FIRST_PAGE = 14;   // أول صفحة فيها header كامل = مساحة أقل للصفوف
    const ROWS_OTHER_PAGES = 18;  // الصفحات التالية فيها header مكرر بنفس الحجم
    const pages: any[][] = [];
    let i = 0;
    while (i < employees.length) {
      const size = pages.length === 0 ? ROWS_FIRST_PAGE : ROWS_OTHER_PAGES;
      pages.push(employees.slice(i, i + size));
      i += size;
    }
    if (pages.length === 0) pages.push([]);

    printWindow.document.write(`
<!DOCTYPE html>
<html dir="${isRTL ? 'rtl' : 'ltr'}">
<head>
<meta charset="UTF-8">
<title>${reportData.payrollNumber} - ${branchLine}</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Segoe UI', Tahoma, Arial, sans-serif; padding: 18px; direction: ${isRTL ? 'rtl' : 'ltr'}; color: #1a1a1a; background: #fff; }
  .header { text-align: center; border-bottom: 3px solid #00796B; padding-bottom: 14px; margin-bottom: 18px; }
  .header img { max-height: 60px; margin-bottom: 6px; }
  .header h2 { color: #00796B; margin: 4px 0; font-size: 20px; }
  .header .subtitle { font-size: 11px; color: #555; }
  /* ✅ نستخدم table layout (لا flex/grid) لأن Chrome لا يكرر thead عند وجودهما */
  table.title-bar { width: 100%; border-collapse: collapse; background: linear-gradient(135deg, #00796B 0%, #00ACC1 100%); color: #fff; border-radius: 8px; margin-bottom: 14px; overflow: hidden; }
  table.title-bar td { padding: 12px 20px; vertical-align: middle; }
  table.title-bar h3 { font-size: 18px; margin: 0; }
  table.title-bar .meta { font-size: 12px; opacity: 0.95; }
  table.stats-tbl { width: 100%; border-collapse: separate; border-spacing: 6px 0; margin-bottom: 14px; }
  table.stats-tbl td { width: 20%; padding: 0; vertical-align: top; }
  .stat-card { padding: 10px 12px; border-radius: 8px; color: #fff; text-align: center; box-shadow: 0 2px 6px rgba(0,0,0,0.08); }
  .stat-card .label { font-size: 10px; opacity: 0.95; margin-bottom: 4px; }
  .stat-card .val { font-size: 16px; font-weight: bold; }
  .s-emp { background: linear-gradient(135deg, #6A1B9A, #AB47BC); }
  .s-earn { background: linear-gradient(135deg, #00796B, #26A69A); }
  .s-ded { background: linear-gradient(135deg, #C62828, #EF5350); }
  .s-net { background: linear-gradient(135deg, #1565C0, #42A5F5); }
  .s-bank { background: linear-gradient(135deg, #EF6C00, #FFA726); }
  table.main { width: 100%; border-collapse: collapse; font-size: 10px; }
  table.main thead { display: table-header-group; }
  tfoot { display: table-row-group; }
  table.main thead th.repeat-header { padding: 0 !important; border: none !important; background: #fff !important; }
  table.main thead th { background: #00796B; color: #fff; padding: 8px 5px; text-align: center; font-weight: 600; border: 1px solid #00564E; font-size: 10px; }
  table.main thead th.group-hours { background: #5E35B1; }
  table.main thead th.group-earn { background: #00796B; }
  table.main thead th.group-ded { background: #C62828; }
  table.main thead th.group-net { background: #1565C0; }
  table.main tbody td { padding: 6px 5px; border: 1px solid #DDD; text-align: center; }
  table.main tbody tr.odd td { background: #FAFAFA; }
  table.main tbody tr.even td { background: #fff; }
  table.main tbody tr.emp-row:hover td { background: #E0F7FA; }
  .cell-code { color: #00796B; font-weight: bold; text-align: ${isRTL ? 'right' : 'left'} !important; padding-${isRTL ? 'right' : 'left'}: 8px !important; }
  .cell-name { text-align: ${isRTL ? 'right' : 'left'} !important; padding-${isRTL ? 'right' : 'left'}: 8px !important; }
  .cell-num { color: #5E35B1; font-weight: 600; }
  .cell-amt { text-align: ${isRTL ? 'left' : 'right'} !important; padding-${isRTL ? 'left' : 'right'}: 8px !important; font-variant-numeric: tabular-nums; }
  .cell-amt.basic { color: #00796B; }
  .cell-amt.allow { color: #00897B; }
  .cell-amt.ot { color: #5E35B1; }
  .cell-amt.earn { color: #00695C; background: #E8F5E9 !important; }
  .cell-amt.ded { color: #C62828; }
  .cell-amt.net { color: #1565C0; background: #E3F2FD !important; font-size: 11px; }
  .cell-amt.bank { color: #EF6C00; }
  .cell-amt.cash { color: #F57C00; }
  tfoot tr td { background: linear-gradient(135deg, #00796B, #00ACC1) !important; color: #fff !important; font-weight: bold; padding: 10px 5px !important; font-size: 11px !important; border: 1px solid #00564E !important; }
  tfoot tr td.cell-amt { text-align: ${isRTL ? 'left' : 'right'} !important; }
  .footer { margin-top: 28px; display: flex; justify-content: space-between; gap: 30px; }
  .sig { text-align: center; flex: 1; }
  .sig .line { border-top: 1.5px solid #333; margin-top: 50px; padding-top: 6px; font-weight: bold; font-size: 11px; color: #333; }
  .footer-meta { margin-top: 14px; text-align: center; font-size: 9px; color: #999; }
  .page-break { page-break-before: always; break-before: page; height: 0; }
  @media print {
    @page { size: A4 landscape; margin: 10mm; }
    body { padding: 0; }
    table.main tbody tr { page-break-inside: avoid; }
    .footer { page-break-inside: avoid; }
    .print-page { page-break-after: auto; }
  }
</style>
</head>
<body>

${pages.map((pageEmps, pageIdx) => {
  const isLast = pageIdx === pages.length - 1;
  const headerBlock = `
    ${companyInfo ? `
    <div class="header">
      ${companyInfo.logoPath ? `<img src="${config.API_ORIGIN}${companyInfo.logoPath}" onerror="this.style.display='none'" />` : ''}
      <h2>${isRTL ? companyInfo.companyNameAr : companyInfo.companyName}</h2>
      <div class="subtitle">
        ${companyInfo.address ? companyInfo.address + ' | ' : ''}${companyInfo.phone ? '📞 ' + companyInfo.phone + ' | ' : ''}${companyInfo.email ? '✉️ ' + companyInfo.email : ''}${companyInfo.website ? ' | 🌐 ' + companyInfo.website : ''}
      </div>
    </div>
    ` : ''}
    <table class="title-bar"><tr>
      <td style="text-align:${isRTL ? 'right' : 'left'};"><h3>${isRTL ? '📊 كشف رواتب' : '📊 Payroll Report'} - ${reportData.payrollNumber}</h3></td>
      <td style="text-align:${isRTL ? 'left' : 'right'};"><div class="meta"><strong>${branchLine}</strong> &nbsp;|&nbsp; <span style="background:rgba(255,255,255,0.25);padding:2px 10px;border-radius:10px;font-weight:bold;">${filterLabel}</span> &nbsp;|&nbsp; ${isRTL ? 'الفترة' : 'Period'}: ${periodStr}${pages.length > 1 ? ` &nbsp;|&nbsp; <span style="background:rgba(255,255,255,0.25);padding:2px 10px;border-radius:10px;">${isRTL ? `صفحة ${pageIdx + 1} من ${pages.length}` : `Page ${pageIdx + 1} of ${pages.length}`}</span>` : ''}</div></td>
    </tr></table>
    <table class="stats-tbl"><tr>
      <td><div class="stat-card s-emp"><div class="label">${isRTL ? 'الموظفين' : 'Employees'}</div><div class="val">${employees.length}</div></div></td>
      <td><div class="stat-card s-earn"><div class="label">${isRTL ? 'إجمالي المستحقات' : 'Total Earnings'}</div><div class="val">${fmt(fEarnings)}</div></div></td>
      <td><div class="stat-card s-ded"><div class="label">${isRTL ? 'إجمالي الخصومات' : 'Total Deductions'}</div><div class="val">${fmt(fDeductions)}</div></div></td>
      <td><div class="stat-card s-net"><div class="label">${isRTL ? 'صافي الرواتب' : 'Net Salary'}</div><div class="val">${fmt(fNet)}</div></div></td>
      <td><div class="stat-card s-bank"><div class="label">${
        printPaymentFilter === 'bank' ? (isRTL ? 'إجمالي البنك' : 'Total Bank') :
        printPaymentFilter === 'cash' ? (isRTL ? 'إجمالي النقدي' : 'Total Cash') :
        (isRTL ? 'بنك / نقدي' : 'Bank / Cash')
      }</div><div class="val" style="font-size:12px;">${
        printPaymentFilter === 'bank' ? fmt(totalBankAll) :
        printPaymentFilter === 'cash' ? fmt(totalCashAll) :
        `${fmt(totalBankAll)} / ${fmt(totalCashAll)}`
      }</div></div></td>
    </tr></table>
  `;
  const colHeader = `
    <thead>
      <tr>
        <th rowspan="2">${isRTL ? 'الكود' : 'Code'}</th>
        <th rowspan="2">${isRTL ? 'الموظف' : 'Employee'}</th>
        <th colspan="4" class="group-hours">${isRTL ? 'الساعات' : 'Hours'}</th>
        <th colspan="4" class="group-earn">${isRTL ? 'المستحقات (KWD)' : 'Earnings (KWD)'}</th>
        <th class="group-ded">${isRTL ? 'الخصومات' : 'Deductions'}</th>
        <th class="group-net">${isRTL ? 'الصافي' : 'Net'}</th>
        ${paymentColSpan > 0 ? `<th colspan="${paymentColSpan}">${isRTL ? 'الدفع' : 'Payment'}</th>` : ''}
      </tr>
      <tr>
        <th class="group-hours">${isRTL ? 'عادي' : 'Reg'}</th>
        <th class="group-hours">${isRTL ? 'إضافي' : 'OT'}</th>
        <th class="group-hours">${isRTL ? 'راحة' : 'Off'}</th>
        <th class="group-hours">${isRTL ? 'عطلة' : 'Hol'}</th>
        <th>${isRTL ? 'أساسي' : 'Basic'}</th>
        <th>${isRTL ? 'بدلات' : 'Allow'}</th>
        <th>${isRTL ? 'OT+راحة+عطلة' : 'OT+Off+Hol'}</th>
        <th>${isRTL ? 'الإجمالي' : 'Total'}</th>
        <th class="group-ded">${isRTL ? 'الإجمالي' : 'Total'}</th>
        <th class="group-net">${isRTL ? 'KWD' : 'KWD'}</th>
        ${showBankCol ? `<th>${isRTL ? 'بنك' : 'Bank'}</th>` : ''}
        ${showCashCol ? `<th>${isRTL ? 'نقدي' : 'Cash'}</th>` : ''}
      </tr>
    </thead>
  `;
  const bodyRows = pageEmps.map((emp: any, idx: number) => renderRow(emp, idx)).join('');
  const footerRow = isLast ? `
    <tfoot>
      <tr>
        <td colspan="6" style="text-align:${isRTL ? 'right' : 'left'};padding-${isRTL ? 'right' : 'left'}:12px !important;">${isRTL ? `الإجمالي (${employees.length} موظف)` : `TOTALS (${employees.length} employees)`}</td>
        <td class="cell-amt">${fmt(employees.reduce((s: number, e: any) => s + (e.basicSalary || 0), 0))}</td>
        <td class="cell-amt">${fmt(employees.reduce((s: number, e: any) => s + (e.housingAllowance || 0) + (e.transportAllowance || 0) + (e.otherAllowances || 0), 0))}</td>
        <td class="cell-amt">${fmt(employees.reduce((s: number, e: any) => s + (e.overtimePay || 0) + (e.publicHolidayPay || 0) + (e.offDayPay || 0), 0))}</td>
        <td class="cell-amt">${fmt(fEarnings)}</td>
        <td class="cell-amt">${fmt(fDeductions)}</td>
        <td class="cell-amt">${fmt(fNet)}</td>
        ${showBankCol ? `<td class="cell-amt">${fmt(totalBankAll)}</td>` : ''}
        ${showCashCol ? `<td class="cell-amt">${fmt(totalCashAll)}</td>` : ''}
      </tr>
    </tfoot>
  ` : '';
  const signaturesBlock = isLast ? `
    <div class="footer">
      <div class="sig"><div class="line">${isRTL ? 'المحاسب' : 'Accountant'}</div></div>
      <div class="sig"><div class="line">${isRTL ? 'المدير المالي' : 'Financial Manager'}</div></div>
      <div class="sig"><div class="line">${isRTL ? 'المدير العام' : 'General Manager'}</div></div>
    </div>
    <div class="footer-meta">
      ${isRTL ? 'تم الإنشاء في' : 'Generated'}: ${new Date().toLocaleString(isRTL ? 'ar-KW' : 'en-GB')} &nbsp;|&nbsp; ${reportData.payrollNumber}
    </div>
  ` : '';
  const pageBreak = pageIdx > 0 ? '<div class="page-break"></div>' : '';
  return `${pageBreak}<div class="print-page">${headerBlock}<table class="main">${colHeader}<tbody>${bodyRows}</tbody>${footerRow}</table>${signaturesBlock}</div>`;
}).join('')}

</body>
</html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); }, 600);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!reportData) {
    return (
      <Box sx={{ p: 3 }}>
        <Paper sx={{ p: 4 }}>
          <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold', color: '#00796B', textAlign: 'center' }}>
            📊 {t.title}
          </Typography>
          
          <Box sx={{ maxWidth: 600, mx: 'auto', mt: 4 }}>
            <Autocomplete
              options={payrolls}
              getOptionLabel={(option) => `${option.payrollNumber} - ${option.type === 'Management' ? (isRTL ? 'إداري' : 'Management') : option.branchName || option.branchNameAr} (${formatPeriod(option.periodStart, option.periodEnd)})`}
              value={payrolls.find(p => p.payrollId === selectedPayrollId) || null}
              onChange={(_, newValue) => {
                if (newValue) {
                  setSelectedPayrollId(newValue.payrollId);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t.selectPayroll}
                  variant="outlined"
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: '1.1rem',
                      '& fieldset': { borderColor: '#00796B', borderWidth: 2 },
                      '&:hover fieldset': { borderColor: '#00695C' },
                      '&.Mui-focused fieldset': { borderColor: '#00796B' },
                    }
                  }}
                />
              )}
            />
            
            {payrolls.length === 0 && (
              <Alert severity="info" sx={{ mt: 3 }}>
                {isRTL ? 'لا توجد رواتب متاحة' : 'No payrolls available'}
              </Alert>
            )}
          </Box>
        </Paper>
      </Box>
    );
  }

  // تصفية البيانات حسب نوع الموظفين
  const filteredEmployees = reportData.employees.filter((emp: any) => emp.bankFileType === payrollTypeFilter);
  const totalBank = filteredEmployees.reduce((sum: number, emp: any) => sum + emp.bankAmount, 0);
  const totalCash = filteredEmployees.reduce((sum: number, emp: any) => sum + emp.cashAmount, 0);

  return (
    <Box sx={{ p: 3 }}>
      {/* Payroll Selector & Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }} className="no-print">
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#00796B', minWidth: '200px' }}>
          📊 {t.title}
        </Typography>
        
        <Box sx={{ flex: '1 1 400px', minWidth: '300px', maxWidth: '500px' }}>
          <Autocomplete
            options={payrolls}
            getOptionLabel={(option) => `${option.payrollNumber} - ${option.type === 'Management' ? (isRTL ? 'إداري' : 'Management') : (isRTL ? option.branchNameAr : option.branchName)}`}
            value={payrolls.find(p => p.payrollId === selectedPayrollId) || null}
            onChange={(_, newValue) => {
              if (newValue) {
                setSelectedPayrollId(newValue.payrollId);
              }
            }}
            size="small"
            renderInput={(params) => (
              <TextField
                {...params}
                label={t.selectPayroll}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'background.paper',
                    '& fieldset': { borderColor: '#00796B' },
                    '&:hover fieldset': { borderColor: '#00695C' },
                  }
                }}
              />
            )}
          />
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={t.refresh}>
            <IconButton onClick={() => generateReport(selectedPayrollId!)} sx={{ color: '#00796B' }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t.exportExcel}>
            <IconButton onClick={exportToExcel} sx={{ color: '#4CAF50' }}>
              <ExcelIcon />
            </IconButton>
          </Tooltip>
          {/* فلتر طريقة الدفع للطباعة — يطبق على PDF + الطباعة العادية + السليبات */}
          <FormControl size="small" sx={{ minWidth: 160, mx: 1 }}>
            <InputLabel>{t.printFilter}</InputLabel>
            <Select
              value={printPaymentFilter}
              label={t.printFilter}
              onChange={(e) => setPrintPaymentFilter(e.target.value as '' | 'bank' | 'cash')}
            >
              <MenuItem value="">{t.printAll}</MenuItem>
              <MenuItem value="bank"><BankIcon fontSize="small" sx={{ mr: 0.5, color: '#1976D2' }} />{t.printBankOnly}</MenuItem>
              <MenuItem value="cash"><CashIcon fontSize="small" sx={{ mr: 0.5, color: '#F57C00' }} />{t.printCashOnly}</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title={t.exportPdf}>
            <IconButton onClick={exportToPdf} sx={{ color: '#F44336' }}>
              <PdfIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t.print}>
            <IconButton onClick={handlePrint} sx={{ color: '#2196F3' }}>
              <PrintIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t.printEmployeeSlips}>
            <IconButton onClick={printAllEmployeeSlips} sx={{ color: '#9C27B0' }}>
              <SlipsIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t.exportBankFile}>
            <IconButton onClick={exportBurganBankWorkers} sx={{ color: '#FF6F00' }}>
              <CloudDownloadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={isRTL ? 'تصدير ملف الرواتب العادية (Staff)' : 'Export Staff Payroll File'}>
            <IconButton onClick={exportStaffPayroll} sx={{ color: '#4CAF50' }}>
              <SalaryIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>


      {/* Monthly View Section */}
      <Paper sx={{ mb: 3, p: 2, bgcolor: mode === 'dark' ? '#1E3A5F' : '#E8F5E9' }} className="no-print">
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#2E7D32' }}>
          📅 {isRTL ? 'عرض شهري حسب النوع (جميع الفروع)' : 'Monthly View by Type (All Branches)'}
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          {/* Month Selector */}
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>{isRTL ? 'الشهر' : 'Month'}</InputLabel>
            <Select
              value={monthlyViewMonth}
              onChange={(e) => setMonthlyViewMonth(Number(e.target.value))}
              label={isRTL ? 'الشهر' : 'Month'}
            >
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(m => (
                <MenuItem key={m} value={m}>
                  {isRTL 
                    ? ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'][m - 1]
                    : ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'][m - 1]
                  }
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Year Selector */}
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>{isRTL ? 'السنة' : 'Year'}</InputLabel>
            <Select
              value={monthlyViewYear}
              onChange={(e) => setMonthlyViewYear(Number(e.target.value))}
              label={isRTL ? 'السنة' : 'Year'}
            >
              {[2024, 2025, 2026, 2027, 2028, 2029, 2030].map(y => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Employee Type Radio */}
          <FormControl component="fieldset">
            <FormLabel component="legend" sx={{ fontSize: '0.85rem' }}>
              {isRTL ? 'النوع' : 'Type'}
            </FormLabel>
            <RadioGroup
              row
              value={payrollTypeFilter}
              onChange={(e) => setPayrollTypeFilter(e.target.value)}
              sx={{ gap: 1 }}
            >
              <FormControlLabel
                value="Worker"
                control={<Radio size="small" />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <WorkIcon fontSize="small" />
                    <Typography variant="body2">{isRTL ? 'عمال' : 'Workers'}</Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="Regular"
                control={<Radio size="small" />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <BusinessCenterIcon fontSize="small" />
                    <Typography variant="body2">{isRTL ? 'موظفين' : 'Staff'}</Typography>
                  </Box>
                }
              />
            </RadioGroup>
          </FormControl>

          {/* Load Button */}
          <Button
            variant="contained"
            onClick={loadMonthlyData}
            sx={{ 
              bgcolor: '#2E7D32', 
              '&:hover': { bgcolor: '#1B5E20' },
              height: '56px'
            }}
          >
            {isRTL ? '📊 عرض البيانات' : '📊 Load Data'}
          </Button>
        </Box>
      </Paper>



      {/* Report Content - For PDF Export */}
      <Box id="payroll-report-content">
        {/* Company Header (Print) */}
        <Box sx={{ display: 'none' }} className="print-only">
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          {companyInfo?.logoBase64 && (
            <img src={companyInfo.logoBase64} alt="Logo" style={{ width: 80, height: 60 }} />
          )}
          <Typography variant="h4" sx={{ color: '#00796B', fontWeight: 'bold' }}>
            {isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName}
          </Typography>
          <Typography variant="body2">{companyInfo?.address}</Typography>
          <Typography variant="body2">{companyInfo?.phone} | {companyInfo?.email}</Typography>
          <Typography variant="h5" sx={{ mt: 2, color: '#00796B' }}>
            {t.title} - {reportData.payrollNumber}
            {reportData.type === 'Branch' && reportData.branchName && ` (${isRTL ? reportData.branchNameAr : reportData.branchName})`}
          </Typography>
          <Typography variant="body2">
            {t.period}: {formatPeriod(reportData.periodStart, reportData.periodEnd)}
          </Typography>
        </Box>
      </Box>

      {/* Payroll Info Card */}
      <Card sx={{ 
        mb: 3, 
        bgcolor: mode === 'dark' ? '#1E3A5F' : '#E0F2F1',
        color: mode === 'dark' ? '#fff' : 'inherit'
      }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ flex: '1 1 250px', minWidth: '200px' }}>
              <Typography variant="body2" color="text.secondary">{t.payrollNumber}</Typography>
              <Typography variant="h6" sx={{ color: '#00796B', fontWeight: 'bold' }}>
                {reportData.payrollNumber}
              </Typography>
            </Box>
            <Box sx={{ flex: '1 1 250px', minWidth: '200px' }}>
              <Typography variant="body2" color="text.secondary">{t.period}</Typography>
              <Typography variant="h6">
                {new Date(reportData.periodStart).toLocaleDateString('en-GB')} → {new Date(reportData.periodEnd).toLocaleDateString('en-GB')}
              </Typography>
            </Box>
            <Box sx={{ flex: '1 1 250px', minWidth: '200px' }}>
              <Typography variant="body2" component="div" color="text.secondary">{t.type}</Typography>
              <Chip 
                label={
                  reportData.type === 'Monthly' 
                    ? (isRTL ? `${payrollTypeFilter === 'Worker' ? 'عمال' : 'موظفين'} - جميع الفروع` : `${payrollTypeFilter === 'Worker' ? 'Workers' : 'Staff'} - All Branches`)
                    : reportData.type === 'Management' ? t.management : t.branch
                } 
                color="primary"
                sx={{ bgcolor: reportData.type === 'Monthly' ? '#2E7D32' : '#00796B' }}
              />
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <Box sx={{ flex: '1 1 250px', minWidth: '200px' }}>
          <Card sx={{ bgcolor: '#4CAF50', color: 'white', height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EmployeesIcon sx={{ fontSize: 45 }} />
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                    {filteredEmployees.length}
                  </Typography>
                  <Typography variant="body2">{t.totalEmployees}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: '1 1 250px', minWidth: '200px' }}>
          <Card sx={{ bgcolor: '#00796B', color: 'white', height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EarningsIcon sx={{ fontSize: 45 }} />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {formatNumber(reportData.totalEarnings)}
                  </Typography>
                  <Typography variant="body2">{t.totalEarnings}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: '1 1 250px', minWidth: '200px' }}>
          <Card sx={{ bgcolor: '#F44336', color: 'white', height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DeductionsIcon sx={{ fontSize: 45 }} />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {formatNumber(reportData.totalDeductions)}
                  </Typography>
                  <Typography variant="body2">{t.totalDeductions}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: '1 1 250px', minWidth: '200px' }}>
          <Card sx={{ bgcolor: '#9C27B0', color: 'white', height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <NetIcon sx={{ fontSize: 45 }} />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {formatNumber(reportData.totalNetSalary)}
                  </Typography>
                  <Typography variant="body2">{t.netSalary}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Payment Split Cards */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
          <Card sx={{ bgcolor: '#1976D2', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <BankIcon sx={{ fontSize: 40 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2">{t.totalBank}</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    {formatCurrency(totalBank)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
          <Card sx={{ bgcolor: '#FF9800', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CashIcon sx={{ fontSize: 40 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2">{t.totalCash}</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    {formatCurrency(totalCash)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Employees Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: mode === 'dark' ? '#00695C' : '#00796B' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold', width: 50 }}></TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.employeeCode}</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.employeeName}</TableCell>
              <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>
                <SalaryIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                {t.basicSalary}
              </TableCell>
              <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>
                <OvertimeIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                {t.overtime}
              </TableCell>
              <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>
                <EarningsIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                {t.totalEarnings}
              </TableCell>
              <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>
                <AbsenceIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                {t.absences}
              </TableCell>
              <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>
                <DeductionIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                {t.penalties}
              </TableCell>
              <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>
                <NetIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                {t.netSalary}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredEmployees.map((emp: any, index: number) => (
              <React.Fragment key={emp.employeeId}>
                <TableRow
                  sx={{ 
                    bgcolor: index % 2 === 0 
                      ? (mode === 'dark' ? '#1E3A5F' : '#E3F2FD')
                      : (mode === 'dark' ? '#263238' : '#FFFFFF'),
                    cursor: 'pointer',
                    '&:hover': { 
                      bgcolor: mode === 'dark' ? '#37474F' : '#B3E5FC'
                    }
                  }}
                  onClick={() => toggleRow(emp.employeeId)}
                >
                  <TableCell>
                    <IconButton size="small">
                      {expandedRows.has(emp.employeeId) ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                    </IconButton>
                  </TableCell>
                  <TableCell>{emp.employeeCode}</TableCell>
                  <TableCell>{isRTL ? emp.employeeNameAr : emp.employeeName}</TableCell>
                  <TableCell align="right">
                    <Chip 
                      label={formatNumber(emp.basicSalary)} 
                      size="small" 
                      sx={{ 
                        bgcolor: mode === 'dark' ? '#1B5E20' : '#E8F5E9', 
                        color: mode === 'dark' ? '#A5D6A7' : '#2E7D32',
                        fontWeight: 'bold'
                      }} 
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Chip 
                      label={formatNumber(emp.overtimePay)} 
                      size="small" 
                      sx={{ 
                        bgcolor: mode === 'dark' ? '#E65100' : '#FFF3E0', 
                        color: mode === 'dark' ? '#FFCC80' : '#F57C00',
                        fontWeight: 'bold'
                      }} 
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Chip 
                      label={formatNumber(emp.totalEarnings)} 
                      size="small" 
                      sx={{ 
                        bgcolor: mode === 'dark' ? '#00695C' : '#00796B', 
                        color: 'white', 
                        fontWeight: 'bold' 
                      }} 
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Chip 
                      label={formatNumber(emp.absencesAmount)} 
                      size="small" 
                      sx={{ 
                        bgcolor: mode === 'dark' ? '#B71C1C' : '#FFEBEE', 
                        color: mode === 'dark' ? '#FFCDD2' : '#C62828',
                        fontWeight: 'bold'
                      }} 
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Chip 
                      label={formatNumber(emp.penaltiesAmount)} 
                      size="small" 
                      sx={{ 
                        bgcolor: mode === 'dark' ? '#880E4F' : '#FCE4EC', 
                        color: mode === 'dark' ? '#F48FB1' : '#AD1457',
                        fontWeight: 'bold'
                      }} 
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Chip 
                      label={formatNumber(emp.netSalary)} 
                      size="small" 
                      sx={{ 
                        bgcolor: mode === 'dark' ? '#6A1B9A' : '#9C27B0', 
                        color: 'white', 
                        fontWeight: 'bold' 
                      }} 
                    />
                  </TableCell>
                </TableRow>

                {/* Expanded Row Details */}
                <TableRow>
                  <TableCell colSpan={9} sx={{ p: 0, border: 0 }}>
                    <Collapse in={expandedRows.has(emp.employeeId)} timeout="auto" unmountOnExit>
                      <Box sx={{ 
                        p: 2, 
                        bgcolor: mode === 'dark' ? '#263238' : '#F5F5F5',
                        color: mode === 'dark' ? '#fff' : 'inherit'
                      }}>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                          {/* Earnings Breakdown */}
                          <Box sx={{ flex: '1 1 350px', minWidth: '300px' }}>
                            <Paper sx={{ 
                              p: 2, 
                              bgcolor: mode === 'dark' ? '#37474F' : 'white',
                              color: mode === 'dark' ? '#fff' : 'inherit'
                            }}>
                              <Typography variant="subtitle2" sx={{ mb: 1, color: mode === 'dark' ? '#4DB6AC' : '#00796B', fontWeight: 'bold' }}>
                                💰 {t.totalEarnings}
                              </Typography>
                              <Divider sx={{ mb: 1 }} />
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <Typography variant="body2"><SalaryIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />{t.basicSalary}:</Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{formatNumber(emp.basicSalary)}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <Typography variant="body2"><HousingIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />{t.housing}:</Typography>
                                  <Typography variant="body2">{formatNumber(emp.housingAllowance)}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <Typography variant="body2"><TransportIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />{t.transport}:</Typography>
                                  <Typography variant="body2">{formatNumber(emp.transportAllowance)}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <Typography variant="body2"><OvertimeIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />{t.overtime}:</Typography>
                                  <Typography variant="body2">{formatNumber(emp.overtimePay)}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <Typography variant="body2"><OffDayIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />{t.offDay}:</Typography>
                                  <Typography variant="body2">{formatNumber(emp.offDayPay)}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <Typography variant="body2"><HolidayIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />{t.holiday}:</Typography>
                                  <Typography variant="body2">{formatNumber(emp.publicHolidayPay)}</Typography>
                                </Box>
                                <Divider sx={{ my: 0.5 }} />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: mode === 'dark' ? '#4DB6AC' : '#00796B' }}>{t.total}:</Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: mode === 'dark' ? '#4DB6AC' : '#00796B' }}>{formatNumber(emp.totalEarnings)}</Typography>
                                </Box>
                              </Box>
                            </Paper>
                          </Box>

                          {/* Deductions Breakdown */}
                          <Box sx={{ flex: '1 1 350px', minWidth: '300px' }}>
                            <Paper sx={{ 
                              p: 2, 
                              bgcolor: mode === 'dark' ? '#37474F' : 'white',
                              color: mode === 'dark' ? '#fff' : 'inherit'
                            }}>
                              <Typography variant="subtitle2" sx={{ mb: 1, color: mode === 'dark' ? '#EF5350' : '#F44336', fontWeight: 'bold' }}>
                                📉 {t.totalDeductions}
                              </Typography>
                              <Divider sx={{ mb: 1 }} />
                              
                              {/* Absences */}
                              {emp.absencesDetails && emp.absencesDetails.length > 0 && (
                                <Box sx={{ mb: 1 }}>
                                  <Typography variant="caption" sx={{ fontWeight: 'bold', color: mode === 'dark' ? '#EF5350' : '#F44336' }}>
                                    {t.absenceDetails}:
                                  </Typography>
                                  <List dense>
                                    {emp.absencesDetails.map((absence: any, idx: number) => (
                                      <ListItem key={idx} sx={{ py: 0.5 }}>
                                        <ListItemIcon sx={{ minWidth: 30 }}>
                                          <AbsenceIcon fontSize="small" color="error" />
                                        </ListItemIcon>
                                        <ListItemText
                                          primary={
                                            <Typography variant="caption">
                                              {new Date(absence.absenceDate).toLocaleDateString('en-GB')} - 
                                              {isRTL ? absence.absenceTypeAr : absence.absenceType}
                                              {absence.days > 0 && ` (${absence.days} ${t.days})`}
                                              {absence.hours > 0 && ` (${absence.hours} ${t.hrs})`}
                                            </Typography>
                                          }
                                          secondary={
                                            <Typography variant="caption" sx={{ color: '#F44336', fontWeight: 'bold' }}>
                                              {formatNumber(absence.deductionAmount)} {t.kwd}
                                            </Typography>
                                          }
                                        />
                                      </ListItem>
                                    ))}
                                  </List>
                                </Box>
                              )}

                              {/* Deductions */}
                              {emp.deductionsDetails && emp.deductionsDetails.length > 0 && (
                                <Box>
                                  <Typography variant="caption" sx={{ fontWeight: 'bold', color: mode === 'dark' ? '#EF5350' : '#F44336' }}>
                                    {t.deductionDetails}:
                                  </Typography>
                                  <List dense>
                                    {emp.deductionsDetails.map((deduction: any, idx: number) => (
                                      <ListItem key={idx} sx={{ py: 0.5 }}>
                                        <ListItemIcon sx={{ minWidth: 30 }}>
                                          <DeductionIcon fontSize="small" color="error" />
                                        </ListItemIcon>
                                        <ListItemText
                                          primary={
                                            <Typography variant="caption">
                                              {new Date(deduction.deductionDate).toLocaleDateString('en-GB')} - 
                                              {isRTL ? deduction.deductionTypeAr : deduction.deductionType}
                                              {deduction.isRecurring && ` (${deduction.installmentNumber}/${deduction.totalInstallments})`}
                                            </Typography>
                                          }
                                          secondary={
                                            <Typography variant="caption" sx={{ color: '#F44336', fontWeight: 'bold' }}>
                                              {formatNumber(deduction.amount)} {t.kwd}
                                              {deduction.referenceNumber && ` - Ref: ${deduction.referenceNumber}`}
                                            </Typography>
                                          }
                                        />
                                      </ListItem>
                                    ))}
                                  </List>
                                </Box>
                              )}

                              <Divider sx={{ my: 0.5 }} />
                              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', color: mode === 'dark' ? '#EF5350' : '#F44336' }}>{t.total}:</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', color: mode === 'dark' ? '#EF5350' : '#F44336' }}>{formatNumber(emp.totalDeductions)}</Typography>
                              </Box>
                            </Paper>
                          </Box>

                          {/* Payment Split */}
                          <Box sx={{ flex: '1 1 100%', width: '100%' }}>
                            <Alert severity="info" icon={<ReceiptIcon />}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
                                <Box>
                                  <Typography variant="caption">{t.bank}:</Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    {formatCurrency(emp.bankAmount)}
                                  </Typography>
                                </Box>
                                <Box>
                                  <Typography variant="caption">{t.cash}:</Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    {formatCurrency(emp.cashAmount)}
                                  </Typography>
                                </Box>
                                <Box>
                                  <Typography variant="caption">{t.netSalary}:</Typography>
                                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#9C27B0' }}>
                                    {formatCurrency(emp.netSalary)}
                                  </Typography>
                                </Box>
                              </Box>
                            </Alert>
                          </Box>
                        </Box>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      </Box>{/* End of payroll-report-content */}

      {/* Employee Selection Dialog */}
      <Dialog 
        open={employeeSelectionDialog !== 'none'} 
        onClose={() => setEmployeeSelectionDialog('none')}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold', color: '#00796B' }}>
          {t.selectEmployees}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
            <Button 
              size="small" 
              variant="outlined" 
              onClick={handleSelectAll}
              sx={{ borderColor: '#00796B', color: '#00796B' }}
            >
              {t.selectAll}
            </Button>
            <Button 
              size="small" 
              variant="outlined" 
              onClick={handleDeselectAll}
              sx={{ borderColor: '#F44336', color: '#F44336' }}
            >
              {t.deselectAll}
            </Button>
            <Chip 
              label={`${selectedEmployeeIds.size} / ${reportData?.employees.length || 0}`}
              color="primary"
              size="small"
            />
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
            {reportData?.employees.map((emp: any) => (
              <FormControlLabel
                key={emp.employeeId}
                control={
                  <Checkbox
                    checked={selectedEmployeeIds.has(emp.employeeId)}
                    onChange={() => handleEmployeeToggle(emp.employeeId)}
                    sx={{ 
                      color: '#00796B',
                      '&.Mui-checked': { color: '#00796B' }
                    }}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {emp.employeeCode} - {isRTL ? emp.employeeNameAr : emp.employeeName}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#666' }}>
                      {t.netSalary}: {formatCurrency(emp.netSalary)}
                    </Typography>
                  </Box>
                }
                sx={{
                  display: 'block',
                  mb: 1,
                  p: 1,
                  border: '1px solid #E0E0E0',
                  borderRadius: 1,
                  '&:hover': { bgcolor: '#E0F2F1', '& .MuiTypography-root': { color: '#00796B' } }
                }}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => setEmployeeSelectionDialog('none')}
            sx={{ color: '#666' }}
          >
            {isRTL ? 'إلغاء' : 'Cancel'}
          </Button>
          <Button 
            onClick={employeeSelectionDialog === 'pdf' ? generatePrintablePDF : printSelectedSlips}
            variant="contained"
            disabled={selectedEmployeeIds.size === 0}
            sx={{ 
              bgcolor: '#00796B', 
              '&:hover': { bgcolor: '#00695C' },
              '&:disabled': { bgcolor: '#BDBDBD' }
            }}
          >
            {t.printSelected} ({selectedEmployeeIds.size})
          </Button>
        </DialogActions>
      </Dialog>

      {/* Burgan Bank Date Selection Dialog */}
      <Dialog 
        open={burganDateDialog} 
        onClose={() => setBurganDateDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#00796B', color: 'white' }}>
          {isRTL ? 'اختر شهر وسنة الراتب' : 'Select Payroll Month & Year'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', gap: 2, flexDirection: 'column' }}>
            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
                {isRTL ? 'الشهر:' : 'Month:'}
              </Typography>
              <select
                value={burganMonth}
                onChange={(e) => setBurganMonth(parseInt(e.target.value))}
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '16px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  direction: isRTL ? 'rtl' : 'ltr'
                }}
              >
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(m => (
                  <option key={m} value={m}>
                    {isRTL 
                      ? ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'][m - 1]
                      : ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'][m - 1]
                    }
                  </option>
                ))}
              </select>
            </Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
                {isRTL ? 'السنة:' : 'Year:'}
              </Typography>
              <input
                type="number"
                value={burganYear}
                onChange={(e) => setBurganYear(parseInt(e.target.value) || new Date().getFullYear())}
                min={2020}
                max={2100}
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '16px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  direction: isRTL ? 'rtl' : 'ltr'
                }}
              />
            </Box>
            <Alert severity="info" sx={{ mt: 1 }}>
              <Typography variant="body2">
                {isRTL 
                  ? `سيتم تصدير الملف باسم: bdinar_format.txt`
                  : `File will be exported as: bdinar_format.txt`
                }
              </Typography>
              {reportData && (
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#666' }}>
                  {isRTL
                    ? `فترة الراتب: ${burganMonth.toString().padStart(2, '0')}/${burganYear} (من ${new Date(burganYear, burganMonth - 1, 1).toLocaleDateString('en-GB')} إلى ${new Date(burganYear, burganMonth, 0).toLocaleDateString('en-GB')})`
                    : `Payroll period: ${burganMonth.toString().padStart(2, '0')}/${burganYear} (${new Date(burganYear, burganMonth - 1, 1).toLocaleDateString('en-GB')} to ${new Date(burganYear, burganMonth, 0).toLocaleDateString('en-GB')})`
                  }
                </Typography>
              )}
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => setBurganDateDialog(false)}
            sx={{ color: '#666' }}
          >
            {isRTL ? 'إلغاء' : 'Cancel'}
          </Button>
          <Button 
            onClick={handleBurganExport}
            variant="contained"
            sx={{ 
              bgcolor: '#00796B', 
              '&:hover': { bgcolor: '#00695C' }
            }}
          >
            {isRTL ? 'تصدير الملف' : 'Export File'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Staff Payroll Date Selection Dialog */}
      <Dialog 
        open={staffDateDialog} 
        onClose={() => setStaffDateDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#4CAF50', color: 'white' }}>
          {isRTL ? 'اختر شهر وسنة الراتب (الموظفÙŠÙ†)' : 'Select Payroll Month & Year (Staff)'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', gap: 2, flexDirection: 'column' }}>
            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
                {isRTL ? 'الشهر:' : 'Month:'}
              </Typography>
              <select
                value={staffMonth}
                onChange={(e) => setStaffMonth(parseInt(e.target.value))}
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '16px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  direction: isRTL ? 'rtl' : 'ltr'
                }}
              >
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(m => (
                  <option key={m} value={m}>
                    {isRTL 
                      ? ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'][m - 1]
                      : ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'][m - 1]
                    }
                  </option>
                ))}
              </select>
            </Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
                {isRTL ? 'السنة:' : 'Year:'}
              </Typography>
              <input
                type="number"
                value={staffYear}
                onChange={(e) => setStaffYear(parseInt(e.target.value) || new Date().getFullYear())}
                min={2020}
                max={2100}
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '16px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  direction: isRTL ? 'rtl' : 'ltr'
                }}
              />
            </Box>
            <Alert severity="info" sx={{ mt: 1 }}>
              <Typography variant="body2">
                {isRTL 
                  ? `سيتم تصدير الملف باسم: bbformat.txt`
                  : `File will be exported as: bbformat.txt`
                }
              </Typography>
              {reportData && (
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#666' }}>
                  {isRTL
                    ? `فترة الراتب: ${staffMonth.toString().padStart(2, '0')}/${staffYear} (من ${new Date(staffYear, staffMonth - 1, 1).toLocaleDateString('en-GB')} إلى ${new Date(staffYear, staffMonth, 0).toLocaleDateString('en-GB')})`
                    : `Payroll period: ${staffMonth.toString().padStart(2, '0')}/${staffYear} (${new Date(staffYear, staffMonth - 1, 1).toLocaleDateString('en-GB')} to ${new Date(staffYear, staffMonth, 0).toLocaleDateString('en-GB')})`
                  }
                </Typography>
              )}
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => setStaffDateDialog(false)}
            sx={{ color: '#666' }}
          >
            {isRTL ? 'إلغاء' : 'Cancel'}
          </Button>
          <Button 
            onClick={handleStaffExport}
            variant="contained"
            sx={{ 
              bgcolor: '#4CAF50', 
              '&:hover': { bgcolor: '#45A049' }
            }}
          >
            {isRTL ? 'تصدير الملف' : 'Export File'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PayrollReport;